import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { SparePartModel } from '../../../models/spare-part-model/spare-part-model';
import { SparePartsService } from 'src/app/services/spareParts/spare-parts.service';
import { OrderOutModel } from 'src/app/models/order-out-model/order-out-model';
import { OrdersService } from 'src/app/services/orders/orders.service';
import { ExportedFoldersService } from 'src/app/services/exported-folders/exported-folders.service';
import { finalize, map, switchMap, concatMap, mergeMap, tap, toArray } from 'rxjs/operators';
import { Observable, of, from } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';
import { ALDANA_COLOR_OPTIONS } from 'src/app/constants/aldana-colors';
import { LabelsService, SparePartForLabel } from 'src/app/services/labels/labels.service';
import { ColumnVisibilityHelper, ColumnVisibilityOption } from 'src/app/utils/column-visibility.helper';
import { toCanonicalSparePartKeys, applyPostEmergentCommentDuplicateDefaults, normalizeFantUrgency, defaultFantUrgency, FantUrgency } from 'src/app/utils/spare-part-payload.util';
import { getReceptionStatusFieldClass } from 'src/app/utils/spare-part-reception.util';
import { readApiErrorMessage, SPARE_PART_DELETE_BLOCKED_MESSAGE, sparePartHasPackageOrPrintedLabel } from 'src/app/utils/spare-part-packaging.util';
import { RETURN_LOG_STATUS_OPTIONS, RETURN_STATUS_OPTIONS } from 'src/app/utils/return-options.util';
import { formatWallClockDateTime } from 'src/app/utils/wall-clock-datetime.util';
interface MultiEditSparePartRow {
  sparePart: SparePartModel;
  netInput: string;
  grossInput: string;
  countryInput: string;
  packageGroupInput: string;
}

interface ResolvedPackageGroup {
  orderId: number;
  packageGroup: number;
  exportNumber: string;
  packageNumber: string;
  rows: MultiEditSparePartRow[];
}

@Component({
  selector: 'app-spare-part-history',
  templateUrl: './spare-part-history.component.html',
  styleUrls: ['./spare-part-history.component.css']
})
export class SparePartHistoryComponent implements OnInit, OnChanges, OnDestroy {
  private readonly DELAY_ALERTS_STORAGE_KEY = 'sparePartDelayAlertsDismissed';
  private readonly COLUMN_VISIBILITY_STORAGE_KEY = 'sparePartHistory.visibleColumns.v7';
  private readonly PREVIOUS_COLUMN_VISIBILITY_STORAGE_KEY = 'sparePartHistory.visibleColumns.v6';

  @Input() orders: OrderOutModel[] = [];

  spareParts: SparePartModel[] = [];
  filteredSpareParts: SparePartModel[] = [];
  displayedSpareParts: SparePartModel[] = [];
  totalSpareParts: number = 0;
  filteredTotal: number = 0;
  totalSparePartsInDB: number = 0; // Total de repuestos en la base de datos

  searchTerm: string = '';
  selectedSparePart: SparePartModel | null = null;
  sortedBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  loading: boolean = false;
  openSparePartModal: boolean = false;
  isDuplicatingSparePart: boolean = false;
  duplicateSparePartTargetOrderNumber = '';
  isFiltering: boolean = false;
  exportMenuOpen: boolean = false;
  openAssignExportModal: boolean = false;
  assigningExportInfo: boolean = false;
  importingRemitosFromExcel: boolean = false;
  openBulkEditModal: boolean = false;
  applyingBulkEdit: boolean = false;
  isCopyingWhatsapp: boolean = false;
  exportNumberToAssign: string = '';
  exportDateToAssign: string = '';
  sparePartDeliveryStatus = environment.sparePartDeliveryStatus;
  aldanaColorOptions = ALDANA_COLOR_OPTIONS;
  private readonly bulkTextAppendSeparator = ' | ';
  bulkEditForm = {
    productStatus: '',
    shippingStatus: '',
    receptionStatus: '',
    color: '',
    interno: '',
    internoAppend: false,
    externo: '',
    externoAppend: false,
    control: '',
    anulado: '',
    recompra: '',
    sendYesOrNo: '',
    ncm: '',
    taxNote: '',
    exportNumber: '',
    returnStatus: '',
    returnLogStatus: ''
  };
  readonly returnStatusOptions = RETURN_STATUS_OPTIONS;
  readonly returnLogStatusOptions = RETURN_LOG_STATUS_OPTIONS;

  /** Modal: edición por repuesto (peso líquido, bruto, país) + generación de paquetes */
  openMultiEditModal = false;
  loadingMultiEditModal = false;
  applyingMultiEditSave = false;
  generatingPackagesFromMultiEdit = false;
  multiEditPhysicalSaved = false;
  /** Si true, al cerrar la modal se recarga el listado (evita refrescar con la modal abierta). */
  multiEditListRefreshPending = false;
  multiEditRows: MultiEditSparePartRow[] = [];

  allSelected: boolean = false;
  selectedItems: SparePartModel[] = [];

  // Nueva propiedad para seguir el último repuesto editado
  lastEditedSparePartId: number | null = null;

  /** Fila con foco estilo Excel (resaltado gris), por sparePartId */
  activeFocusedRowId: number | null = null;

  pageSize: number = 100;
  currentPage: number = 1;
  isLoadingMore: boolean = false;
  hasMoreItems: boolean = true;
  totalPages: number = 1;

  allFilteredCount: number = 0; // Nueva propiedad para almacenar el conteo total de repuestos filtrados
  isSelectingAllFiltered: boolean = false; // Indica si estamos seleccionando todos los repuestos filtrados

  /**
   * IDs destildados a mano mientras isSelectingAllFiltered está activo.
   * En ese modo la selección no es una lista de ítems sino "todo lo que matchea el filtro",
   * así que las exclusiones hay que llevarlas aparte: sin esto, destildar una fila solo
   * cambiaba el visual y la operación masiva igual la incluía.
   */
  deselectedIds: Set<number> = new Set<number>();

  /** Cantidad real de repuestos sobre los que va a operar la acción masiva. */
  get selectedCount(): number {
    return this.isSelectingAllFiltered
      ? Math.max(0, this.allFilteredCount - this.deselectedIds.size)
      : this.selectedItems.length;
  }

  /** Quita del conjunto bajado los que el usuario destildó. */
  private excludeDeselected(spareParts: SparePartModel[]): SparePartModel[] {
    if (this.deselectedIds.size === 0) {
      return spareParts;
    }
    return spareParts.filter(sp => !this.deselectedIds.has(sp.sparePartId));
  }

  // Hacer disponible Math para la plantilla
  Math = Math;

  // Agregar nuevas propiedades para almacenar los totales
  totalCost: number = 0;
  totalSalesValue: number = 0;

  // Filtros estilo Excel (igual que tabla órdenes, con multi-selección)
  columnFilters: { [key: string]: any } = {};
  activeFilterMenu: string | null = null;
  filterMenuPosition: { x: number, y: number } = { x: 0, y: 0 };
  /** Altura máxima del popup para no cortarse (calculada según espacio en viewport). */
  filterMenuMaxHeight: number = 400;
  filterSearchTerm: string = '';
  /** Selección temporal en el popup; solo se escribe en columnFilters al pulsar Aplicar */
  tempColumnFilterSelection: string[] = [];

  // Valores únicos para dropdowns
  uniqueFornecedores: string[] = [];
  uniqueVendedores: string[] = [];
  uniqueEstados: string[] = [];
  uniqueEstadoEnvio: string[] = [];
  uniquePaises: string[] = [];
  uniqueExportNumbers: string[] = [];
  uniqueColumnValues: { [key: string]: string[] } = {};
  /** Placeholder "Cargando valores..." en popup de filtro por columna (igual que órdenes). */
  loadingUniqueValues = false;
  private readonly EMPTY_FILTER_VALUE = '(vacío)';
  /** Si true, solo se muestran repuestos de los últimos 60 días; filtros aplican sobre ese subconjunto. */
  onlyLast60Days = false;
  /** Si true, solo se muestran repuestos con coeficiente V.Venta/P.Unitario <= 11. */
  onlyLowSaleRatio = false;
  private readonly CLIENT_SIDE_CALCULATED_COLUMNS = new Set<string>(['diasPrazo', 'diasAtraso']);
  /** Cache base para evitar recargar todas las páginas en cada filtro de columna. */
  private allSparePartsBaseCache: SparePartModel[] | null = null;
  private allSparePartsBaseCacheKey = '';
  private temporarySparePartIdSeed = -1;
  private dismissedDelayAlerts = new Set<string>();
  columnSelectorOpen = false;
  columnVisibilityCss = '';
  readonly columnVisibilityOptions: ColumnVisibilityOption[] = [
    { id: 'clienteUY', label: 'Cliente Uy', columnIndex: 4 },
    { id: 'orderDate', label: 'Data Ordem', columnIndex: 5 },
    { id: 'transportType', label: 'Vehiculo', columnIndex: 6 },
    { id: 'carregamento', label: 'Carga', columnIndex: 7 },
    { id: 'exportNumber', label: 'Num EXP', columnIndex: 8 },
    { id: 'fechaExportacion', label: 'F. Exp.', columnIndex: 9 },
    { id: 'diasAtraso', label: 'D. Atr.', columnIndex: 10 },
    { id: 'diasPrazo', label: 'D. Pzo.', columnIndex: 11 },
    { id: 'orderNumber', label: 'Ordem', columnIndex: 12 },
    { id: 'siniestro', label: 'Sinistro', columnIndex: 13 },
    { id: 'sparePartName', label: 'Nombre', columnIndex: 14 },
    { id: 'productCode', label: 'Cód. Peça', columnIndex: 15 },
    { id: 'amount', label: 'Quant', columnIndex: 16 },
    { id: 'unitPrice', label: 'P. Unit', columnIndex: 17 },
    { id: 'valorDeVenta', label: 'V. Vend', columnIndex: 18 },
    { id: 'netWheight', label: 'P. Neto', columnIndex: 19 },
    { id: 'grossWheight', label: 'P. Bruto', columnIndex: 20 },
    { id: 'productStatus', label: 'Estado', columnIndex: 21 },
    { id: 'lastExportedAt', label: 'Exp. en', columnIndex: 22 },
    { id: 'shippingStatus', label: 'E. Envio', columnIndex: 23 },
    { id: 'receptionStatus', label: 'Recepción', columnIndex: 24 },
    { id: 'arrivalDate', label: 'Fecha de llegada', columnIndex: 25 },
    { id: 'packager', label: 'Empaquetador', columnIndex: 26 },
    { id: 'returnStatus', label: 'E. Dev.', columnIndex: 27 },
    { id: 'returnLogStatus', label: 'E. Dev LOG', columnIndex: 28 },
    { id: 'returnReason', label: 'Mot. Dev.', columnIndex: 29 },
    { id: 'returnCarrier', label: 'Transp.', columnIndex: 30 },
    { id: 'nfd', label: 'NFD', columnIndex: 31 },
    { id: 'mlStatus', label: 'Sit. ML', columnIndex: 32 },
    { id: 'mlDeadline', label: 'Prazo ML', columnIndex: 33 },
    { id: 'freteDev', label: 'FRETE DEV', columnIndex: 34 },
    { id: 'returnDetails', label: 'Det. Adic.', columnIndex: 35 },
    { id: 'expectedArrivalDate', label: 'F. Esp.', columnIndex: 36 },
    { id: 'comments', label: 'Comentarios', columnIndex: 37 },
    { id: 'emergentComment', label: 'Comentario emergente', columnIndex: 38 },
    { id: 'interno', label: 'Interno', columnIndex: 39 },
    { id: 'externo', label: 'Externo', columnIndex: 40 },
    { id: 'fechaDeComunicado', label: 'F. Com.', columnIndex: 41 },
    { id: 'color', label: 'Color', columnIndex: 42 },
    { id: 'vendedor', label: 'Vendedor', columnIndex: 43 },
    { id: 'fornecedor', label: 'Fornecedor', columnIndex: 44 },
    { id: 'codBr', label: 'Cod BR', columnIndex: 45 },
    { id: 'taxNote', label: 'Nota Fiscal', columnIndex: 46 },
    { id: 'ncm', label: 'NCM', columnIndex: 47 },
    { id: 'country', label: 'Pais', columnIndex: 48 },
    { id: 'packageNumber', label: 'N. Pqt.', columnIndex: 49 },
    { id: 'isDamaged', label: 'Esta dañado', columnIndex: 50 },
    { id: 'needReview', label: 'Need Review', columnIndex: 51 },
    { id: 'isFant', label: 'FANT?', columnIndex: 52 },
    { id: 'fantUrgency', label: 'Urgencia FANT', columnIndex: 53 },
    { id: 'recompra', label: 'Recompra', columnIndex: 54 },
    { id: 'control', label: 'Control', columnIndex: 55 },
    { id: 'anulado', label: 'Anulado', columnIndex: 56 },
    { id: 'trello', label: 'Trello', columnIndex: 57 },
    { id: 'devZureo', label: 'DevZureo', columnIndex: 58 },
    { id: 'entradaSage', label: 'Entrada Sage', columnIndex: 59 },
    { id: 'controleSeba', label: 'Controle Seba', columnIndex: 60 },
    { id: 'okAnulacao', label: 'Ok Anulação', columnIndex: 61 },
    { id: 'facturado', label: 'Facturado', columnIndex: 62 },
    { id: 'workshopName', label: 'Taller', columnIndex: 63 },
    { id: 'sendYesOrNo', label: 'Enviar', columnIndex: 64 }
  ];
  private readonly columnVisibilityHelper = new ColumnVisibilityHelper(
    this.COLUMN_VISIBILITY_STORAGE_KEY,
    'spare-main-table',
    this.columnVisibilityOptions
  );
  visibleColumnIds = new Set<string>();
  private columnVisibilityStyleElement: HTMLStyleElement | null = null;

  constructor(
    private sparePartService: SparePartsService,
    private orderService: OrdersService,
    private exportedFoldersService: ExportedFoldersService,
    private labelsService: LabelsService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.loadDismissedDelayAlerts();
    this.loadColumnVisibility();
    this.loading = true;

    // Obtener el conteo total de repuestos en la base de datos (o últimos 60 días si onlyLast60Days)
    this.sparePartService.getTotalSparePartsCount(this.onlyLast60Days ? 60 : undefined, this.onlyLowSaleRatio).subscribe(
      count => {
        this.totalSparePartsInDB = count;

        // Cargar la primera página de repuestos
        this.loadSparePartsPage(this.currentPage);
      },
      error => {
        console.error('Error al obtener el conteo total de repuestos:', error);
        this.loading = false;
        this.showErrorMessage('Error al obtener el conteo total de repuestos. Por favor, intente nuevamente.');
      }
    );
  }

  private normalizeDateOnly(dateValue: Date | string | undefined): Date | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }

  getDelayDays(sparePart: SparePartModel): number {
    const exportDate = this.normalizeDateOnly(sparePart.fechaExportacion);
    const arrivalDate = this.normalizeDateOnly(sparePart.expectedArrivalDate);
    if (!exportDate || !arrivalDate) return 0;
    const diffMs = exportDate.getTime() - arrivalDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }

  private loadDismissedDelayAlerts(): void {
    try {
      const raw = sessionStorage.getItem(this.DELAY_ALERTS_STORAGE_KEY);
      if (!raw) {
        this.dismissedDelayAlerts = new Set<string>();
        return;
      }
      const parsed = JSON.parse(raw);
      this.dismissedDelayAlerts = new Set<string>(Array.isArray(parsed) ? parsed : []);
    } catch {
      this.dismissedDelayAlerts = new Set<string>();
    }
  }

  private persistDismissedDelayAlerts(): void {
    try {
      sessionStorage.setItem(
        this.DELAY_ALERTS_STORAGE_KEY,
        JSON.stringify(Array.from(this.dismissedDelayAlerts))
      );
    } catch {
      // Ignore storage failures.
    }
  }

  private loadColumnVisibility(): void {
    this.migrateColumnVisibility();
    this.ensureNewColumnVisible('fantUrgency', 'sparePartHistory.fantUrgencyColumnMigrated');
    this.visibleColumnIds = this.columnVisibilityHelper.loadVisibleColumnIds();
    this.rebuildColumnVisibilityCss();
  }

  /** Copia la visibilidad guardada y deja visible la columna nueva sin resetear el resto. */
  private migrateColumnVisibility(): void {
    try {
      if (localStorage.getItem(this.COLUMN_VISIBILITY_STORAGE_KEY)) {
        return;
      }

      const previous = localStorage.getItem(this.PREVIOUS_COLUMN_VISIBILITY_STORAGE_KEY);
      if (!previous) {
        return;
      }

      const parsed = JSON.parse(previous);
      if (!Array.isArray(parsed)) {
        return;
      }

      const next = parsed.filter((columnId: unknown) => typeof columnId === 'string') as string[];
      if (!next.includes('emergentComment')) {
        next.push('emergentComment');
      }

      localStorage.setItem(this.COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Ignore storage failures.
    }
  }

  /** Muestra una columna nueva una sola vez, sin volver a abrirla si el usuario la oculta después. */
  private ensureNewColumnVisible(columnId: string, migrationFlag: string): void {
    try {
      if (localStorage.getItem(migrationFlag)) {
        return;
      }

      const raw = localStorage.getItem(this.COLUMN_VISIBILITY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && !parsed.includes(columnId)) {
          parsed.push(columnId);
          localStorage.setItem(this.COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(parsed));
        }
      }

      localStorage.setItem(migrationFlag, '1');
    } catch {
      // Ignore storage failures.
    }
  }

  getEmergentCommentDisplay(sparePart: SparePartModel): string {
    const own = (sparePart?.emergentComment || '').trim();
    if (own) {
      return own;
    }

    return (sparePart?.effectiveEmergentComment || '').trim();
  }

  private persistColumnVisibility(): void {
    this.columnVisibilityHelper.persistVisibleColumnIds(this.visibleColumnIds);
  }

  private rebuildColumnVisibilityCss(): void {
    this.columnVisibilityCss = this.columnVisibilityHelper.buildCss(this.visibleColumnIds);
    this.syncColumnVisibilityStyleElement();
  }

  private syncColumnVisibilityStyleElement(): void {
    if (!this.columnVisibilityStyleElement) {
      const existing = document.getElementById('spare-part-history-column-visibility-style') as HTMLStyleElement | null;
      this.columnVisibilityStyleElement = existing ?? document.createElement('style');
      this.columnVisibilityStyleElement.id = 'spare-part-history-column-visibility-style';
      if (!existing) {
        document.head.appendChild(this.columnVisibilityStyleElement);
      }
    }

    this.columnVisibilityStyleElement.textContent = this.columnVisibilityCss || '';
  }

  isColumnVisible(columnId: string): boolean {
    return this.columnVisibilityHelper.isVisible(this.visibleColumnIds, columnId);
  }

  toggleColumnSelector(): void {
    this.columnSelectorOpen = !this.columnSelectorOpen;
  }

  toggleColumnVisibility(columnId: string): void {
    this.visibleColumnIds = this.columnVisibilityHelper.toggleColumn(this.visibleColumnIds, columnId);
    this.persistColumnVisibility();
    this.rebuildColumnVisibilityCss();
  }

  ngOnDestroy(): void {
    if (this.columnVisibilityStyleElement?.parentNode) {
      this.columnVisibilityStyleElement.parentNode.removeChild(this.columnVisibilityStyleElement);
    }
    this.columnVisibilityStyleElement = null;
  }

  getDelaySeverity(sparePart: SparePartModel): 'yellow' | 'orange' | 'red' | null {
    const delayDays = this.getDelayDays(sparePart);
    if (delayDays >= 20) return 'red';
    if (delayDays >= 10) return 'orange';
    if (delayDays >= 5) return 'yellow';
    return null;
  }

  private buildDelayAlertKey(sparePart: SparePartModel, severity: 'yellow' | 'orange' | 'red'): string {
    return `sparePart:${sparePart.sparePartId}:${severity}`;
  }

  isDelayAlertDismissed(sparePart: SparePartModel): boolean {
    const severity = this.getDelaySeverity(sparePart);
    if (!severity) return false;
    return this.dismissedDelayAlerts.has(this.buildDelayAlertKey(sparePart, severity));
  }

  hasDelayAlert(sparePart: SparePartModel): boolean {
    return !!this.getDelaySeverity(sparePart) && !this.isDelayAlertDismissed(sparePart);
  }

  dismissDelayAlert(event: Event, sparePart: SparePartModel): void {
    event.stopPropagation();
    const severity = this.getDelaySeverity(sparePart);
    if (!severity) return;
    this.dismissedDelayAlerts.add(this.buildDelayAlertKey(sparePart, severity));
    this.persistDismissedDelayAlerts();
  }

  getDelayIconClass(sparePart: SparePartModel): string {
    if (!this.hasDelayAlert(sparePart)) return '';
    const severity = this.getDelaySeverity(sparePart);
    if (severity === 'yellow') return 'text-yellow-500';
    if (severity === 'orange') return 'text-orange-500';
    if (severity === 'red') return 'text-red-500';
    return '';
  }

  getDelayCellClass(sparePart: SparePartModel): string {
    if (!this.hasDelayAlert(sparePart)) return '';
    const severity = this.getDelaySeverity(sparePart);
    if (severity === 'yellow') return 'bg-yellow-100';
    if (severity === 'orange') return 'bg-orange-100';
    if (severity === 'red') return 'bg-red-100';
    return '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orders'] && this.orders && this.orders.length > 0) {
      this.processOrders();
    }
  }

  /**
   * Normaliza un valor numérico, convirtiendo texto con formatos como "1.235,35" a un número como 1235.35
   * @param value - El valor a normalizar
   * @returns número normalizado
   */
  normalizeNumericValue(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    let stringValue = String(value).trim().replace(/[^0-9,.-]/g, '');
    if (!stringValue) {
      return 0;
    }

    const lastComma = stringValue.lastIndexOf(',');
    const lastDot = stringValue.lastIndexOf('.');

    // Si conviven ambos separadores, el último se toma como decimal y el otro como miles.
    if (lastComma !== -1 && lastDot !== -1) {
      if (lastComma > lastDot) {
        stringValue = stringValue.replace(/\./g, '').replace(',', '.');
      } else {
        stringValue = stringValue.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      // Solo coma: se interpreta como decimal.
      stringValue = stringValue.replace(',', '.');
    }

    const parsed = parseFloat(stringValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Maneja la entrada de valores numéricos en campos de texto
   * @param event - Evento de entrada
   */
  handleNumericInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const initialValue = input.value;

    // Preservar posición del cursor
    const cursorPosition = input.selectionStart;

    // Permitir solo dígitos, comas, puntos
    let value = initialValue.replace(/[^0-9,.-]/g, '');

    // Reemplazar comas por puntos
    value = value.replace(/,/g, '.');

    // Asegurar que solo hay un punto decimal
    const parts = value.split('.');
    if (parts.length > 2) {
      // Si hay más de un punto, conservamos solo el primero
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Asegurar que solo haya 2 decimales después del punto
    if (parts.length === 2 && parts[1].length > 2) {
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Si hay más de 2 decimales, movemos los dígitos excedentes al entero
      const newIntegerPart = integerPart + decimalPart.substring(0, decimalPart.length - 2);
      const newDecimalPart = decimalPart.substring(decimalPart.length - 2);
      value = newIntegerPart + '.' + newDecimalPart;
    }

    // Si el valor ha cambiado, actualizar el campo
    if (value !== initialValue) {
      const oldLength = initialValue.length;
      input.value = value;

      // Restaurar posición del cursor, ajustando si se modificó el valor
      if (cursorPosition !== null) {
        const newPosition = Math.min(cursorPosition + (value.length - oldLength), value.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }
  }

  private processOrders() {
    this.spareParts = [];
    let totalProcessed = 0;

    this.orders.forEach(order => {
      if (!order.spareParts) return;

      order.spareParts.forEach(sparePart => {
        totalProcessed++;
        this.spareParts.push({
          ...sparePart,
          orderNumber: order.orderNumber
        });
      });
    });

    this.spareParts.sort((a, b) => {
      const dateA = new Date(a.expectedArrivalDate || 0);
      const dateB = new Date(b.expectedArrivalDate || 0);
      return dateB.getTime() - dateA.getTime();
    });

    this.totalSpareParts = this.spareParts.length;

    const statusDistribution = this.spareParts.reduce((acc, sp) => {
      const status = this.normalizeString(sp.productStatus);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    this.applyFilters(false);
  }

  // Método para cargar una página específica de repuestos
  loadSparePartsPage(page: number) {
    if (this.hasActiveClientSideProcessing()) {
      this.loadSparePartsPageWithClientSideProcessing(page);
      return;
    }

    this.loading = true;

    this.sparePartService.getPaginatedSpareParts(
      page,
      this.pageSize,
      this.searchTerm,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getServerSideSortColumn(),
      this.sortOrder,
      this.getServerColumnFilters(),
      this.onlyLast60Days ? 60 : undefined,
      this.onlyLowSaleRatio
    )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe({
        next: (response) => {
          // Verificar que response.spareParts existe antes de intentar el mapeo
          if (!response || !response.spareParts) {
            console.error('Error: La respuesta del servidor no contiene la propiedad spareParts', response);
            this.showErrorMessage('Error al cargar los repuestos. La respuesta del servidor es incorrecta.');
            this.displayedSpareParts = [];
            return;
          }

          this.displayedSpareParts = response.spareParts;
          this.filteredSpareParts = response.spareParts;
          this.clearSparePartsTableSelectionState();
          this.filteredTotal = response.totalCount;
          this.totalPages = Math.ceil(response.totalCount / this.pageSize);
          this.currentPage = response.page;
          this.totalCost = Number(response.totalCost ?? 0);
          this.totalSalesValue = Number(response.totalSalesValue ?? 0);

          // Si no tenemos el total de repuestos en la BD, usamos el total de la respuesta
          if (this.totalSparePartsInDB === 0) {
            this.totalSparePartsInDB = response.totalCount;
          }

          this.updateTotals();
        },
        error: (error) => {
          console.error('Error al cargar repuestos paginados:', error);
          this.showErrorMessage('Error al cargar los repuestos. Por favor, intente nuevamente.');
        }
      });
  }

  private loadSparePartsPageWithClientSideProcessing(page: number): void {
    this.loading = true;
    this.currentPage = page;

    this.fetchAllFilteredSpareParts()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe({
        next: (allSpareParts) => {
          const processed = this.applyColumnFiltersAndSortToSparePartList(allSpareParts);
          this.filteredSpareParts = processed;
          this.filteredTotal = processed.length;
          this.totalPages = Math.max(1, Math.ceil(this.filteredTotal / this.pageSize));

          if (this.currentPage > this.totalPages) {
            this.currentPage = 1;
          }

          this.updateDisplayedPage();
          this.calculateTotalsFromList(this.filteredSpareParts);
          this.updateTotals();
        },
        error: (error) => {
          console.error('Error al cargar repuestos con procesamiento en cliente:', error);
          this.showErrorMessage('Error al cargar los repuestos. Por favor, intente nuevamente.');
        }
      });
  }

  // Método auxiliar para cargar solo repuestos sin asociarlos a órdenes
  private loadSpareParts(page: number) {
    this.sparePartService.getPaginatedSpareParts(
      page,
      this.pageSize,
      this.searchTerm,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getServerSideSortColumn(),
      this.sortOrder,
      this.getServerColumnFilters(),
      this.onlyLast60Days ? 60 : undefined,
      this.onlyLowSaleRatio
    )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
        })
      )
      .subscribe(
        response => {
          if (!response || !response.spareParts) {
            console.error('Error: La respuesta del servidor no contiene la propiedad spareParts', response);
            this.showErrorMessage('Error al cargar los repuestos. La respuesta del servidor es incorrecta.');
            this.displayedSpareParts = [];
            return;
          }

          this.displayedSpareParts = response.spareParts;
          this.filteredSpareParts = response.spareParts;
          this.clearSparePartsTableSelectionState();
          this.filteredTotal = response.totalCount;
          this.totalPages = Math.ceil(response.totalCount / this.pageSize);
          this.currentPage = response.page;
          this.totalCost = Number(response.totalCost ?? 0);
          this.totalSalesValue = Number(response.totalSalesValue ?? 0);

          if (this.totalSparePartsInDB === 0) {
            this.totalSparePartsInDB = response.totalCount;
          }

          this.updateTotals();
        },
        error => {
          console.error('Error al cargar repuestos paginados:', error);
          this.showErrorMessage('Error al cargar los repuestos. Por favor, intente nuevamente.');
        }
      );
  }

  private hasActiveColumnFilters(): boolean {
    return Object.values(this.columnFilters).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value === null || value === undefined) {
        return false;
      }
      return String(value).trim() !== '';
    });
  }

  isSparePartColumnFiltered(column: string): boolean {
    const value = this.columnFilters[column];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (value === null || value === undefined) {
      return false;
    }
    return String(value).trim() !== '';
  }

  private hasActiveClientSideProcessing(): boolean {
    return false;
  }

  private hasActiveCalculatedColumnFilters(): boolean {
    return Object.entries(this.columnFilters || {}).some(([column, value]) =>
      this.isClientSideCalculatedColumn(column) &&
      ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && !!String(value ?? '').trim()))
    );
  }

  private isClientSideCalculatedColumn(column?: string | null): boolean {
    if (!column) {
      return false;
    }
    return this.CLIENT_SIDE_CALCULATED_COLUMNS.has(column);
  }

  private getServerSideSortColumn(): string | undefined {
    if (!this.sortedBy) {
      return undefined;
    }
    return this.sortedBy;
  }

  private getServerColumnFilters(): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};

    Object.entries(this.columnFilters || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const selected = value
          .map(v => String(v ?? '').trim())
          .filter(v => v !== '');
        if (selected.length > 0) {
          normalized[key] = selected;
        }
      } else if (value !== null && value !== undefined) {
        const raw = String(value).trim();
        if (raw !== '') {
          normalized[key] = [raw];
        }
      }
    });

    return normalized;
  }

  private getServerColumnFiltersExcept(exceptColumn?: string): Record<string, string[]> {
    const normalized = this.getServerColumnFilters();
    if (!exceptColumn) {
      return normalized;
    }

    Object.keys(normalized).forEach(key => {
      if (key.toLowerCase() === exceptColumn.toLowerCase()) {
        delete normalized[key];
      }
    });

    return normalized;
  }

  private buildSparePartsCacheKey(): string {
    return JSON.stringify({
      searchTerm: (this.searchTerm || '').trim(),
      onlyLast60Days: this.onlyLast60Days,
      onlyLowSaleRatio: this.onlyLowSaleRatio,
      sortBy: this.sortedBy || '',
      sortOrder: this.sortOrder,
      columnFilters: this.getServerColumnFilters()
    });
  }

  private invalidateSparePartsCache(): void {
    this.allSparePartsBaseCache = null;
    this.allSparePartsBaseCacheKey = '';
  }

  // Tope de peticiones simultaneas al bajar el dataset completo. Es conservador a
  // proposito: cada peticion en vuelo retiene una conexion del pool de Postgres —25 en
  // produccion— y ejecuta la consulta filtrada cara. Ademas nginx no proxea la API, el
  // navegador negocia HTTP/2 directo contra Kestrel, asi que no hay limite de conexiones
  // del lado del cliente: este numero es la unica proteccion que existe. Con 4, un
  // usuario nunca toma mas de 4 conexiones y hacen falta seis descargas completas
  // simultaneas para acercarse al techo del pool.
  private readonly fullDatasetConcurrency = 4;
  private readonly fullDatasetPageSize = 500;

  private fetchSparePartsPage(page: number) {
    return this.sparePartService.getPaginatedSpareParts(
      page,
      this.fullDatasetPageSize,
      this.searchTerm,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getServerSideSortColumn(),
      this.sortOrder,
      this.getServerColumnFilters(),
      this.onlyLast60Days ? 60 : undefined,
      this.onlyLowSaleRatio
    );
  }

  private fetchAllFilteredSparePartsFromApi(): Observable<SparePartModel[]> {
    return this.fetchSparePartsPage(1).pipe(
      switchMap(firstResponse => {
        const totalPages = firstResponse.totalPages;
        if (totalPages <= 1) {
          return of(firstResponse.spareParts);
        }

        // Las respuestas llegan desordenadas, asi que cada pagina se guarda en su
        // posicion y se concatenan recien al final: el servidor ya aplico el orden que
        // eligio el usuario, y ese orden termina en el Excel y en el copiado a WhatsApp.
        const pagesInOrder: SparePartModel[][] = new Array(totalPages);
        pagesInOrder[0] = firstResponse.spareParts;

        const remainingPages: number[] = [];
        for (let page = 2; page <= totalPages; page++) {
          remainingPages.push(page);
        }

        return from(remainingPages).pipe(
          mergeMap(
            page => this.fetchSparePartsPage(page).pipe(
              tap(response => {
                pagesInOrder[page - 1] = response.spareParts;
              })
            ),
            this.fullDatasetConcurrency
          ),
          toArray(),
          map(() => pagesInOrder.reduce<SparePartModel[]>(
            (all, pageSpareParts) => all.concat(pageSpareParts ?? []),
            []
          ))
        );
      })
    );
  }

  private fetchAllFilteredSpareParts(): Observable<SparePartModel[]> {
    const currentKey = this.buildSparePartsCacheKey();
    if (this.allSparePartsBaseCache && this.allSparePartsBaseCacheKey === currentKey) {
      return of(this.allSparePartsBaseCache);
    }

    return this.fetchAllFilteredSparePartsFromApi().pipe(
      tap(allSpareParts => {
        this.allSparePartsBaseCache = allSpareParts;
        this.allSparePartsBaseCacheKey = currentKey;
      })
    );
  }

  /**
   * Misma regla que loadAllSparePartsWithColumnFilters: solo filtros realmente activos
   * y el mismo orden que ve el usuario en modo procesamiento en cliente.
   */
  private applyColumnFiltersAndSortToSparePartList(allSpareParts: SparePartModel[]): SparePartModel[] {
    let filtered = this.hasActiveColumnFilters()
      ? allSpareParts.filter(sp => this.passesColumnFilters(sp))
      : allSpareParts;
    if (this.sortedBy) {
      filtered = this.sortList(filtered, this.sortedBy, this.sortOrder);
    }
    return filtered;
  }

  /** Conjunto para Excel: mismo fetch que la tabla + filtros; sin caché para datos de orden frescos. */
  private getSparePartsDatasetForExport(): Observable<SparePartModel[]> {
    this.invalidateSparePartsCache();
    return this.fetchAllFilteredSpareParts();
  }

  private loadAllSparePartsWithColumnFilters(): void {
    this.loadSparePartsPage(this.currentPage);
  }

  private updateDisplayedPage(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.displayedSpareParts = this.filteredSpareParts.slice(start, end);
    this.clearSparePartsTableSelectionState();
  }

  private clearSparePartsTableSelectionState(): void {
    this.allSelected = false;
    this.selectedItems = [];
    this.isSelectingAllFiltered = false;
    this.allFilteredCount = 0;
    this.deselectedIds.clear();

    const clearSelection = (list: SparePartModel[] | undefined): void => {
      (list || []).forEach(sparePart => {
        sparePart['selected'] = false;
      });
    };

    clearSelection(this.displayedSpareParts);
    clearSelection(this.filteredSpareParts);
    clearSelection(this.spareParts);
  }

  /**
   * Ordena los datos según la columna seleccionada
   * @param column - Columna por la que ordenar
   */
  // Método específico para ordenar por transportType
  sortTransportType(event: Event): void {
    if (event) {
      event.stopPropagation(); // Prevenir que se propague a la fila
      event.preventDefault(); // Prevenir comportamiento por defecto
    }

    if (this.displayedSpareParts.length === 0) {
      return;
    }

    // Llamar al método de ordenamiento
    this.sortData('transportType');
  }

  sortData(column: string): void {
    // Si hacemos clic en la misma columna, invertimos el orden
    if (this.sortedBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // Si es una nueva columna, establecemos orden ascendente por defecto
      this.sortedBy = column;
      this.sortOrder = 'asc';
    }

    this.currentPage = 1;
    this.invalidateSparePartsCache();
    this.loadSparePartsPage(this.currentPage);
  }

  private sortList(
    list: SparePartModel[],
    column: string,
    order: 'asc' | 'desc'
  ): SparePartModel[] {
    const sortedArray = [...list];
    sortedArray.sort((a, b) => {
      // Caso especial para transportType
      if (column === 'transportType') {
        // Obtener el transportType de diferentes formas posibles
        let valueA: string = '';
        let valueB: string = '';

        // Intentar obtener transportType de múltiples formas
        if (a.transportType) {
          valueA = String(a.transportType).trim().toLowerCase();
        } else if ((a as any).TransportType) {
          valueA = String((a as any).TransportType).trim().toLowerCase();
        }

        if (b.transportType) {
          valueB = String(b.transportType).trim().toLowerCase();
        } else if ((b as any).TransportType) {
          valueB = String((b as any).TransportType).trim().toLowerCase();
        }

        // Definir orden de prioridad: camioneta (1), camion (2), sin transportType (3)
        const getOrder = (value: string): number => {
          if (!value || value === '') return 3;
          if (value === 'camioneta') return 1;
          if (value === 'camion') return 2;
          return 3; // Cualquier otro valor va al final
        };

        const orderA = getOrder(valueA);
        const orderB = getOrder(valueB);

        // Comparar los órdenes
        if (orderA < orderB) {
          return order === 'asc' ? -1 : 1;
        }
        if (orderA > orderB) {
          return order === 'asc' ? 1 : -1;
        }
        // Si tienen el mismo orden, mantener su posición relativa
        return 0;
      } else {
        let valueA: any = a[column as keyof SparePartModel];
        let valueB: any = b[column as keyof SparePartModel];

        if (column === 'emergentComment') {
          valueA = this.getEmergentCommentDisplay(a);
          valueB = this.getEmergentCommentDisplay(b);
        }

        if (column === 'fantUrgency') {
          const rank = (sp: SparePartModel): number => {
            const urgency = this.getFantUrgency(sp);
            if (urgency === 'green') return 1;
            if (urgency === 'yellow') return 2;
            if (urgency === 'red') return 3;
            return 0;
          };
          valueA = rank(a);
          valueB = rank(b);
        }

        if (column === 'isFant' || column === 'recompra' || column === 'control' || column === 'anulado' || column === 'trello' || column === 'devZureo' || column === 'entradaSage' || column === 'controleSeba' || column === 'okAnulacao' || column === 'facturado' || column === 'needReview') {
          const pick = (sp: SparePartModel): boolean => {
            if (column === 'isFant') return !!(sp.isFant ?? (sp as any).IsFant);
            if (column === 'recompra') return this.isRecompra(sp);
            if (column === 'control') return this.isControl(sp);
            if (column === 'anulado') return this.isAnulado(sp);
            if (column === 'devZureo') return this.isDevZureo(sp);
            if (column === 'entradaSage') return this.isEntradaSage(sp);
            if (column === 'controleSeba') return this.isControleSeba(sp);
            if (column === 'okAnulacao') return this.isOkAnulacao(sp);
            if (column === 'facturado') return this.isFacturado(sp);
            if (column === 'needReview') return this.isNeedReview(sp);
            return this.isTrello(sp);
          };
          valueA = pick(a) ? 1 : 0;
          valueB = pick(b) ? 1 : 0;
        }

        // Transformar valores según el tipo de datos
        if (column === 'expectedArrivalDate' || column === 'lastExportedAt' || column === 'fechaExportacion' || column === 'arrivalDate' || column === 'mlDeadline') {
          valueA = valueA ? new Date(valueA).getTime() : 0;
          valueB = valueB ? new Date(valueB).getTime() : 0;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        } else if (column === 'unitPrice' || column === 'valorDeVenta' || column === 'netWheight' || column === 'grossWheight') {
          // Convertir a números utilizando nuestra función de normalización
          valueA = this.normalizeNumericValue(valueA);
          valueB = this.normalizeNumericValue(valueB);
        } else if (column === 'diasPrazo') {
          const pa = this.calculateDiasPrazo(a);
          const pb = this.calculateDiasPrazo(b);
          const na = pa === null || pa === undefined;
          const nb = pb === null || pb === undefined;
          if (na && nb) {
            valueA = 0;
            valueB = 0;
          } else if (na) {
            valueA = order === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
            valueB = pb as number;
          } else if (nb) {
            valueA = pa as number;
            valueB = order === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
          } else {
            valueA = pa as number;
            valueB = pb as number;
          }
        } else if (column === 'diasAtraso') {
          valueA = this.getDelayDays(a);
          valueB = this.getDelayDays(b);
        } else if (column === 'orderDate') {
          valueA = valueA ? new Date(valueA).getTime() : 0;
          valueB = valueB ? new Date(valueB).getTime() : 0;
        }

        // Comparar valores
        if (valueA < valueB) {
          return order === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });
    return sortedArray;
  }

  private showProgressMessage(message: string) {
    const progressMessage = document.createElement('div');
    progressMessage.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded shadow-lg z-50';
    progressMessage.textContent = message;
    document.body.appendChild(progressMessage);
    setTimeout(() => progressMessage.remove(), 2000);
  }

  private showErrorMessage(message: string) {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded shadow-lg z-50';
    errorMessage.textContent = message;
    document.body.appendChild(errorMessage);
    setTimeout(() => errorMessage.remove(), 5000);
  }

  private normalizeString(str: any): string {
    if (str === null || str === undefined) return '';
    return str.toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  private normalizeImportHeader(value: any): string {
    if (value === null || value === undefined) return '';
    return value.toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  getReceptionDisplayValue(sparePart: SparePartModel): string {
    const rawValue = (sparePart.receptionStatus ?? '').toString().trim().toUpperCase();
    if (!rawValue) return 'NO-LLEGO';
    if (rawValue === 'LLEGO' || rawValue === 'LLEGÓ') return 'LLEGO';
    if (rawValue === 'NO-LLEGO' || rawValue === 'NO LLEGO' || rawValue === 'NO_LLEGO' || rawValue === 'AVERIA' || rawValue === 'AVERÍA') return 'NO-LLEGO';
    return 'NO-LLEGO';
  }

  getReceptionStatusFieldClass = getReceptionStatusFieldClass;

  applyFilters(updatePage: boolean = true): void {
    if (updatePage) {
      this.currentPage = 1;
      if (this.hasActiveClientSideProcessing()) {
        this.loadAllSparePartsWithColumnFilters();
      } else {
        this.loadSparePartsPage(this.currentPage);
      }
    }

    // Actualizar los totales con los filtros actuales
    this.updateTotals();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;

    this.currentPage = page;
    this.loadSparePartsPage(page);
  }

  /**
   * Método que se ejecuta cuando cambia el término de búsqueda
   * Ahora solo actualiza el estado de filtrado pero no hace la búsqueda
   */
  onSearchChange(): void {
    // Solo actualizamos el estado de filtrado basado en si hay un término de búsqueda
    this.isFiltering = !!(this.searchTerm && this.searchTerm.trim() !== '');
  }

  /** Aplica el filtro "solo últimos 60 días" y recarga datos (sin limpiar búsqueda). */
  applyOnlyLast60Days(): void {
    this.currentPage = 1;
    this.loading = true;
    this.invalidateSparePartsCache();
    this.sparePartService.getTotalSparePartsCount(this.onlyLast60Days ? 60 : undefined, this.onlyLowSaleRatio).subscribe(
      count => {
        this.totalSparePartsInDB = count;
        if (this.hasActiveClientSideProcessing()) {
          this.loadAllSparePartsWithColumnFilters();
        } else {
          this.loadSparePartsPage(this.currentPage);
        }
        this.updateTotals();
      },
      error => {
        console.error('Error al aplicar filtro 60 días:', error);
        this.loading = false;
        this.showErrorMessage('Error al aplicar el filtro. Intente nuevamente.');
      }
    );
  }

  /** Aplica el filtro "solo coeficiente <= 11" y recarga datos (sin limpiar búsqueda). */
  applyLowSaleRatioFilter(): void {
    this.currentPage = 1;
    this.loading = true;
    this.invalidateSparePartsCache();
    this.sparePartService.getTotalSparePartsCount(this.onlyLast60Days ? 60 : undefined, this.onlyLowSaleRatio).subscribe(
      count => {
        this.totalSparePartsInDB = count;
        if (this.hasActiveClientSideProcessing()) {
          this.loadAllSparePartsWithColumnFilters();
        } else {
          this.loadSparePartsPage(this.currentPage);
        }
        this.updateTotals();
      },
      error => {
        console.error('Error al aplicar filtro de coeficiente <= 11:', error);
        this.loading = false;
        this.showErrorMessage('Error al aplicar el filtro. Intente nuevamente.');
      }
    );
  }

  forceReload(): void {
    this.loading = true;
    this.invalidateSparePartsCache();

    // Limpiar todos los datos
    this.displayedSpareParts = [];
    this.filteredTotal = 0;
    this.searchTerm = '';
    this.currentPage = 1;
    this.clearSparePartsTableSelectionState();

    // Mostrar mensaje de recarga
    this.showProgressMessage('Recargando repuestos...');

    // Obtener el conteo total de repuestos nuevamente (o últimos 60 días si aplica)
    this.sparePartService.getTotalSparePartsCount(this.onlyLast60Days ? 60 : undefined, this.onlyLowSaleRatio).subscribe(
      count => {
        this.totalSparePartsInDB = count;

        // Cargar la primera página de repuestos
        if (this.hasActiveClientSideProcessing()) {
          this.loadAllSparePartsWithColumnFilters();
        } else {
          this.loadSparePartsPage(this.currentPage);
        }
      },
      error => {
        console.error('Error al obtener el conteo total de repuestos:', error);
        this.loading = false;
        this.showErrorMessage('Error al obtener el conteo total de repuestos. Por favor, intente nuevamente.');
      }
    );
  }

  toggleAllSelection(): void {
    // Limpiar la selección actual
    this.selectedItems = [];
    // Marcar o desmarcar todo reinicia siempre las exclusiones previas.
    this.deselectedIds.clear();

    if (this.allSelected) {
      // Si hay filtros de columna activos, el backend NO los conoce (solo recibe searchTerm/fechas).
      // Usar la lista explícita de repuestos filtrados para que solo se actualicen esos IDs.
      if (this.hasActiveColumnFilters()) {
        this.isSelectingAllFiltered = false;
        this.selectedItems = [...this.filteredSpareParts];
        this.displayedSpareParts.forEach(sp => { sp['selected'] = true; });
        return;
      }

      this.isSelectingAllFiltered = true;
      this.allFilteredCount = this.filteredTotal;

      this.displayedSpareParts.forEach(sparePart => {
        sparePart['selected'] = true;
        this.selectedItems.push(sparePart);
      });
    } else {
      // Si estamos deseleccionando, desmarcar la selección masiva
      this.isSelectingAllFiltered = false;
      this.allFilteredCount = 0;

      // Desmarcar todos los visibles
      this.displayedSpareParts.forEach(sparePart => {
        sparePart['selected'] = false;
      });

    }
  }

  toggleSelection(sparePart: SparePartModel): void {
    // En modo "todos los filtrados" la selección no es una lista, así que lo que se
    // registra es la exclusión (o su revocación al volver a tildar).
    if (this.isSelectingAllFiltered) {
      if (sparePart['selected']) {
        this.deselectedIds.delete(sparePart.sparePartId);
      } else {
        this.deselectedIds.add(sparePart.sparePartId);
      }
    }

    // Actualizar la selección individual
    if (sparePart['selected']) {
      // Verificar si el elemento ya está en selectedItems para evitar duplicados
      const exists = this.selectedItems.some(item => item.sparePartId === sparePart.sparePartId);
      if (!exists) {
        this.selectedItems.push(sparePart);
      }
    } else {
      // Eliminar el elemento de selectedItems si ya no está seleccionado
      const index = this.selectedItems.findIndex(item => item.sparePartId === sparePart.sparePartId);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
    }

    // Verificar si todos los repuestos visibles están seleccionados
    this.allSelected = this.displayedSpareParts.length > 0 &&
      this.displayedSpareParts.every(sp => sp['selected']);
  }

  openBulkEditSparePartsModal(): void {
    if (!this.isSelectingAllFiltered && this.selectedItems.length === 0) {
      this.selectedItems = this.displayedSpareParts.filter(item => item['selected']);
    }

    // selectedCount contempla las exclusiones: en modo "todos los filtrados" se puede
    // haber destildado todo lo visible y quedar en cero.
    if (this.selectedCount > 0) {
      this.resetBulkEditForm();
      this.openBulkEditModal = true;
      this.cdr.detectChanges();
      return;
    }

    this.showErrorMessage('No se ha seleccionado ningún repuesto');
  }

  closeBulkEditSparePartsModal(): void {
    if (this.applyingBulkEdit) return;
    this.openBulkEditModal = false;
    this.resetBulkEditForm();
  }

  private resetBulkEditForm(): void {
    this.bulkEditForm = {
      productStatus: '',
      shippingStatus: '',
      receptionStatus: '',
      color: '',
      interno: '',
      internoAppend: false,
      externo: '',
      externoAppend: false,
      control: '',
      anulado: '',
      recompra: '',
      sendYesOrNo: '',
      ncm: '',
      taxNote: '',
      exportNumber: '',
      returnStatus: '',
      returnLogStatus: ''
    };
  }

  private mergeBulkTextField(existingValue: string | undefined | null, newValue: string, append: boolean): string {
    const existing = (existingValue || '').trim();
    const incoming = (newValue || '').trim();
    if (!incoming) {
      return existing;
    }
    if (!append || !existing) {
      return incoming;
    }
    return `${existing}${this.bulkTextAppendSeparator}${incoming}`;
  }

  private parseBulkBooleanValue(value: string): boolean | null {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  selectBulkAldanaColor(color: string): void {
    this.bulkEditForm.color = color;
  }

  clearBulkAldanaColor(): void {
    this.bulkEditForm.color = '';
  }

  getBulkEditSummaryLines(): string[] {
    const summary: string[] = [];
    const color = (this.bulkEditForm.color || '').trim();
    const interno = (this.bulkEditForm.interno || '').trim();
    const externo = (this.bulkEditForm.externo || '').trim();
    const ncm = (this.bulkEditForm.ncm || '').trim();
    const taxNote = (this.bulkEditForm.taxNote || '').trim();
    const exportNumber = (this.bulkEditForm.exportNumber || '').trim();

    if (this.bulkEditForm.productStatus) {
      summary.push(`Estado del producto: ${this.bulkEditForm.productStatus}`);
    }
    if (this.bulkEditForm.shippingStatus) {
      summary.push(`Estado enviado/no enviado: ${this.bulkEditForm.shippingStatus}`);
    }
    if (this.bulkEditForm.receptionStatus) {
      summary.push(`Estado llegó/no llegó: ${this.bulkEditForm.receptionStatus}`);
    }
    if (color) {
      summary.push(`Color: ${color}`);
    }
    if (interno) {
      summary.push(
        `Comentario interno (${this.bulkEditForm.internoAppend ? 'agregar al existente' : 'reemplazar'}): ${interno}`
      );
    }
    if (externo) {
      summary.push(
        `Comentario externo (${this.bulkEditForm.externoAppend ? 'agregar al existente' : 'reemplazar'}): ${externo}`
      );
    }
    if (this.bulkEditForm.control) {
      summary.push(`Check control: ${this.bulkEditForm.control === 'true' ? 'SI' : 'NO'}`);
    }
    if (this.bulkEditForm.anulado) {
      summary.push(`Anulado: ${this.bulkEditForm.anulado === 'true' ? 'SI' : 'NO'}`);
    }
    if (this.bulkEditForm.recompra) {
      summary.push(`Recompra: ${this.bulkEditForm.recompra === 'true' ? 'SI' : 'NO'}`);
    }
    if (this.bulkEditForm.sendYesOrNo) {
      summary.push(`Enviar: ${this.bulkEditForm.sendYesOrNo === 'true' ? 'SI' : 'NO'}`);
    }
    if (ncm) {
      summary.push(`NCM: ${ncm}`);
    }
    if (taxNote) {
      summary.push(`Nota fiscal: ${taxNote}`);
    }
    if (exportNumber) {
      summary.push(`Número de exportación: ${exportNumber}`);
    }
    if (this.bulkEditForm.returnStatus) {
      summary.push(`Estado de devolución: ${this.bulkEditForm.returnStatus}`);
    }
    if (this.bulkEditForm.returnLogStatus) {
      summary.push(`Estado de devolución LOG: ${this.bulkEditForm.returnLogStatus}`);
    }

    return summary;
  }

  private buildBulkEditedSparePart(sparePart: SparePartModel): SparePartModel {
    const color = (this.bulkEditForm.color || '').trim();
    const interno = (this.bulkEditForm.interno || '').trim();
    const externo = (this.bulkEditForm.externo || '').trim();
    const ncm = (this.bulkEditForm.ncm || '').trim();
    const taxNote = (this.bulkEditForm.taxNote || '').trim();
    const exportNumber = (this.bulkEditForm.exportNumber || '').trim();
    const recompraValue = this.parseBulkBooleanValue(this.bulkEditForm.recompra);
    const controlValue = this.parseBulkBooleanValue(this.bulkEditForm.control);
    const anuladoValue = this.parseBulkBooleanValue(this.bulkEditForm.anulado);

    return {
      ...sparePart,
      productStatus: this.bulkEditForm.productStatus || (sparePart.productStatus || ''),
      shippingStatus: this.bulkEditForm.shippingStatus || (sparePart.shippingStatus || ''),
      receptionStatus: this.bulkEditForm.receptionStatus || undefined,
      color: color || (sparePart.color || ''),
      interno: this.mergeBulkTextField(sparePart.interno, interno, this.bulkEditForm.internoAppend),
      externo: this.mergeBulkTextField(sparePart.externo, externo, this.bulkEditForm.externoAppend),
      ncm: ncm || (sparePart.ncm || ''),
      taxNote: taxNote || (sparePart.taxNote || ''),
      exportNumber: exportNumber || (sparePart.exportNumber || ''),
      returnStatus: this.bulkEditForm.returnStatus || (sparePart.returnStatus || ''),
      returnLogStatus: this.bulkEditForm.returnLogStatus || (sparePart.returnLogStatus || ''),
      isFant: !!(sparePart.isFant ?? (sparePart as any).IsFant),
      // undefined cuando el campo del modal quedó vacío: JSON.stringify lo omite y el backend
      // lo recibe como null, es decir "no tocar". Mandar el valor actual, como se hacía antes,
      // pisaba el flag con un valor potencialmente desactualizado si otro usuario lo había
      // cambiado desde que se cargó la lista.
      recompra: recompraValue ?? undefined,
      control: controlValue ?? undefined,
      anulado: anuladoValue ?? undefined,
      sendYesOrNo: this.bulkEditForm.sendYesOrNo || undefined
    };
  }

  applyBulkEditToSelectedSpareParts(): void {
    if (this.applyingBulkEdit) return;

    const summaryLines = this.getBulkEditSummaryLines();
    if (summaryLines.length === 0) {
      this.showErrorMessage('Debes completar al menos un campo para actualizar.');
      return;
    }

    const totalTarget = this.selectedCount;
    const summaryText = summaryLines.map(line => `- ${line}`).join('\n');

    const firstConfirm = confirm(
      `Resumen de cambios para ${totalTarget} repuesto(s):\n\n${summaryText}\n\n¿Desea continuar?`
    );
    if (!firstConfirm) return;

    const secondConfirm = confirm('Confirmación final: esta acción actualizará todos los repuestos seleccionados. ¿Confirmas?');
    if (!secondConfirm) return;

    this.applyingBulkEdit = true;

    const source$ = this.isSelectingAllFiltered
      ? this.fetchAllFilteredSpareParts().pipe(map(all => this.excludeDeselected(all)))
      : of(this.selectedItems.length > 0 ? [...this.selectedItems] : this.displayedSpareParts.filter(item => item['selected']));

    source$
      .pipe(
        switchMap(sparePartsToUpdate => {
          if (!sparePartsToUpdate.length) {
            this.showErrorMessage('No se encontraron repuestos para actualizar.');
            return of(0);
          }

          const updatedSpareParts = sparePartsToUpdate.map(sp => this.buildBulkEditedSparePart(sp));
          return this.sparePartService.updateSpareParts(updatedSpareParts).pipe(
            switchMap(() => of(updatedSpareParts.length))
          );
        }),
        finalize(() => {
          this.applyingBulkEdit = false;
        })
      )
      .subscribe({
        next: (updatedCount) => {
          if (updatedCount <= 0) return;

          this.showProgressMessage(`Se actualizaron ${updatedCount} repuesto(s) en forma masiva.`);
          this.openBulkEditModal = false;
          this.resetBulkEditForm();
          this.clearCheckbox();
          this.isSelectingAllFiltered = false;

          setTimeout(() => {
            this.invalidateSparePartsCache();
            if (this.hasActiveClientSideProcessing()) {
              this.loadAllSparePartsWithColumnFilters();
            } else {
              this.loadSparePartsPage(this.currentPage);
            }
          }, 500);
        },
        error: (error) => {
          console.error('Error al actualizar repuestos en forma masiva:', error);
          const backendMessage = error?.error?.message || error?.error;
          this.showErrorMessage(typeof backendMessage === 'string' ? backendMessage : 'No se pudieron aplicar los cambios masivos.');
        }
      });
  }

  openAssignExportInfoModal(): void {
    this.exportNumberToAssign = '';
    this.exportDateToAssign = '';
    this.openAssignExportModal = true;
  }

  closeAssignExportInfoModal(): void {
    this.openAssignExportModal = false;
    this.assigningExportInfo = false;
  }

  submitAssignExportInfo(): void {
    const exportNumber = (this.exportNumberToAssign || '').trim();
    const exportDate = (this.exportDateToAssign || '').trim();

    if (!exportNumber) {
      this.showErrorMessage('El número de exportación es obligatorio');
      return;
    }

    if (!exportDate) {
      this.showErrorMessage('La fecha de exportación es obligatoria');
      return;
    }

    this.assigningExportInfo = true;
    this.sparePartService.assignExportInfo(exportNumber, exportDate).subscribe({
      next: (response) => {
        const updatedCount = response?.count ?? 0;
        this.showProgressMessage(`Fecha de exportación aplicada a ${updatedCount} repuesto(s)`);
        this.closeAssignExportInfoModal();

        if (this.hasActiveClientSideProcessing()) {
          this.loadAllSparePartsWithColumnFilters();
        } else {
          this.loadSparePartsPage(this.currentPage);
        }
      },
      error: (error) => {
        console.error('Error al asignar exportación masiva:', error);
        this.assigningExportInfo = false;
        const backendMessage = error?.error?.message || error?.error;
        this.showErrorMessage(typeof backendMessage === 'string' ? backendMessage : 'Error al asignar exportación. Intenta nuevamente.');
      }
    });
  }

  triggerRemitosImport(fileInput: HTMLInputElement): void {
    if (this.importingRemitosFromExcel) {
      return;
    }

    fileInput.value = '';
    fileInput.click();
  }

  onRemitosExcelSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      this.showErrorMessage('El archivo debe ser Excel (.xlsx o .xls).');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          this.showErrorMessage('El archivo no contiene hojas para importar.');
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
          header: 1,
          raw: false,
          defval: ''
        });

        if (!rows || rows.length < 2) {
          this.showErrorMessage('El Excel debe incluir encabezados y al menos una fila de datos.');
          return;
        }

        const headerRow = (rows[0] || []).map((value: any) => this.normalizeImportHeader(value));
        const nonEmptyHeaders = headerRow.filter(h => h.length > 0);
        const uniqueHeaders = Array.from(new Set(nonEmptyHeaders));
        const hasOnlyTwoColumns = uniqueHeaders.length === 2 && uniqueHeaders.includes('id') && uniqueHeaders.includes('remito');

        if (!hasOnlyTwoColumns) {
          this.showErrorMessage('El Excel debe tener solo dos columnas: "id" y "remito".');
          return;
        }

        const idIndex = headerRow.findIndex(h => h === 'id');
        const remitoIndex = headerRow.findIndex(h => h === 'remito');
        if (idIndex < 0 || remitoIndex < 0) {
          this.showErrorMessage('No se encontraron las columnas requeridas: id y remito.');
          return;
        }

        const updates: Array<{ sparePartId: number; shippingReceipt: string }> = [];
        const invalidIdRows: number[] = [];
        const emptyRemitoRows: number[] = [];

        for (let i = 1; i < rows.length; i++) {
          const row = rows[i] || [];
          const idRaw = row[idIndex];
          const remitoRaw = row[remitoIndex];

          const idText = (idRaw ?? '').toString().trim();
          const remitoText = (remitoRaw ?? '').toString().trim();

          if (!idText && !remitoText) {
            continue;
          }

          const sparePartId = Number(idText);
          const isValidId = Number.isInteger(sparePartId) && sparePartId > 0;
          if (!isValidId) {
            invalidIdRows.push(i + 1);
            continue;
          }

          if (!remitoText) {
            emptyRemitoRows.push(i + 1);
            continue;
          }

          updates.push({ sparePartId, shippingReceipt: remitoText });
        }

        if (updates.length === 0) {
          this.showErrorMessage('No hay filas válidas para importar.');
          return;
        }

        const duplicateIds = updates
          .map(u => u.sparePartId)
          .filter((id, index, arr) => arr.indexOf(id) !== index)
          .filter((id, index, arr) => arr.indexOf(id) === index)
          .sort((a, b) => a - b);

        if (duplicateIds.length > 0) {
          this.showErrorMessage(`IDs duplicados en el Excel: ${duplicateIds.join(', ')}. Operación cancelada.`);
          return;
        }

        if (invalidIdRows.length > 0) {
          this.showErrorMessage(
            `Hay IDs inválidos en filas: ${invalidIdRows.slice(0, 10).join(', ')}${invalidIdRows.length > 10 ? '...' : ''}.`
          );
          return;
        }

        if (emptyRemitoRows.length > 0) {
          this.showErrorMessage(
            `Hay remitos vacíos en filas: ${emptyRemitoRows.slice(0, 10).join(', ')}${emptyRemitoRows.length > 10 ? '...' : ''}.`
          );
          return;
        }

        const confirmMessage =
          `Se importarán ${updates.length} remito(s).\n` +
          'Si algún ID no existe, se cancelará toda la operación.\n\n' +
          '¿Desea continuar?';
        if (!window.confirm(confirmMessage)) {
          return;
        }

        this.importingRemitosFromExcel = true;
        this.sparePartService.bulkUpdateSparePartShippingReceipts(updates).subscribe({
          next: (response) => {
            const updatedCount = response?.updatedCount ?? updates.length;
            this.showProgressMessage(`Se actualizaron ${updatedCount} remito(s) correctamente.`);

            if (this.hasActiveClientSideProcessing()) {
              this.loadAllSparePartsWithColumnFilters();
            } else {
              this.loadSparePartsPage(this.currentPage);
            }
          },
          error: (error) => {
            console.error('Error al importar remitos por Excel:', error);
            this.importingRemitosFromExcel = false;
            const backendMessage = error?.error?.message || error?.error;
            const missingIds: number[] = error?.error?.missingIds || [];
            const duplicateIdsBackend: number[] = error?.error?.duplicateIds || [];
            const invalidIdsBackend: number[] = error?.error?.invalidIds || [];
            const emptyReceiptIdsBackend: number[] = error?.error?.emptyReceiptIds || [];

            if (missingIds.length > 0) {
              this.showErrorMessage(`IDs inexistentes: ${missingIds.join(', ')}. Operación cancelada.`);
            } else if (duplicateIdsBackend.length > 0) {
              this.showErrorMessage(`IDs duplicados: ${duplicateIdsBackend.join(', ')}. Operación cancelada.`);
            } else if (invalidIdsBackend.length > 0) {
              this.showErrorMessage(`IDs inválidos: ${invalidIdsBackend.join(', ')}. Operación cancelada.`);
            } else if (emptyReceiptIdsBackend.length > 0) {
              this.showErrorMessage(`Remitos vacíos para IDs: ${emptyReceiptIdsBackend.join(', ')}. Operación cancelada.`);
            } else {
              this.showErrorMessage(typeof backendMessage === 'string' ? backendMessage : 'Error al importar remitos.');
            }
          },
          complete: () => {
            this.importingRemitosFromExcel = false;
          }
        });
      } catch (error) {
        console.error('Error al procesar el Excel de remitos:', error);
        this.showErrorMessage('No se pudo procesar el archivo Excel.');
        this.importingRemitosFromExcel = false;
      } finally {
        input.value = '';
      }
    };

    reader.onerror = () => {
      this.showErrorMessage('No se pudo leer el archivo Excel.');
      this.importingRemitosFromExcel = false;
      input.value = '';
    };

    reader.readAsArrayBuffer(file);
  }

  // --- Modal edición múltiple (peso / país) + paquetes ---

  openMultiEditSparePartsModal(): void {
    if (!this.isSelectingAllFiltered && this.selectedItems.length === 0) {
      this.selectedItems = this.displayedSpareParts.filter(item => item['selected']);
    }

    if (this.selectedCount === 0) {
      this.showErrorMessage('No se ha seleccionado ningún repuesto');
      return;
    }

    this.resetMultiEditModalState();
    this.openMultiEditModal = true;
    this.loadingMultiEditModal = true;

    const source$ = this.isSelectingAllFiltered
      ? this.fetchAllFilteredSpareParts().pipe(
          map(all =>
            this.hasActiveColumnFilters() ? all.filter(sp => this.passesColumnFilters(sp)) : all
          ),
          map(all => this.excludeDeselected(all))
        )
      : of([...this.selectedItems]);

    source$
      .pipe(
        finalize(() => {
          this.loadingMultiEditModal = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (list) => {
          const byId = new Map<number, SparePartModel>();
          for (const sp of list) {
            byId.set(sp.sparePartId, sp);
          }
          this.multiEditRows = Array.from(byId.values()).map(sp => ({
            sparePart: { ...sp },
            netInput: '',
            grossInput: '',
            countryInput: '',
            packageGroupInput: ''
          }));
        },
        error: () => {
          this.showErrorMessage('No se pudieron cargar los repuestos para editar.');
          this.openMultiEditModal = false;
        }
      });
  }

  closeMultiEditSparePartsModal(): void {
    if (this.applyingMultiEditSave || this.generatingPackagesFromMultiEdit) {
      return;
    }
    const pendingListRefresh = this.multiEditListRefreshPending;
    this.openMultiEditModal = false;
    this.resetMultiEditModalState();

    if (pendingListRefresh) {
      this.clearCheckbox();
      setTimeout(() => {
        this.invalidateSparePartsCache();
        if (this.hasActiveClientSideProcessing()) {
          this.loadAllSparePartsWithColumnFilters();
        } else {
          this.loadSparePartsPage(this.currentPage);
        }
      }, 400);
    }
  }

  private resetMultiEditModalState(): void {
    this.multiEditRows = [];
    this.multiEditPhysicalSaved = false;
    this.multiEditListRefreshPending = false;
    this.loadingMultiEditModal = false;
    this.applyingMultiEditSave = false;
    this.generatingPackagesFromMultiEdit = false;
  }

  onMultiEditRowChanged(): void {
    this.multiEditPhysicalSaved = false;
  }

  multiEditPieceDisplayName(row: MultiEditSparePartRow): string {
    const sp = row.sparePart;
    return (sp.sparePartNameEs || sp.sparePartName || '').trim() || `ID ${sp.sparePartId}`;
  }

  applyMultiEditPhysical(): void {
    if (this.applyingMultiEditSave) {
      return;
    }

    const updates = this.buildMultiEditPhysicalUpdates();
    if (updates === null) {
      return;
    }
    if (updates.length === 0) {
      this.multiEditPhysicalSaved = true;
      this.showProgressMessage('No había datos nuevos para guardar. Podés generar paquetes.');
      this.cdr.detectChanges();
      return;
    }

    this.applyingMultiEditSave = true;
    this.sparePartService.bulkUpdateSparePartsPhysical(updates).subscribe({
      next: (res) => {
        this.applyingMultiEditSave = false;
        const count = res?.updatedCount ?? 0;
        this.showProgressMessage(`Se guardaron datos de ${count} repuesto(s). Ya podés generar paquetes.`);

        const updatedIds = new Set(updates.map(u => u.sparePartId));
        for (const row of this.multiEditRows) {
          if (!updatedIds.has(row.sparePart.sparePartId)) {
            continue;
          }
          const u = updates.find(x => x.sparePartId === row.sparePart.sparePartId);
          if (!u) {
            continue;
          }
          if (u.netWheight !== undefined) {
            row.sparePart.netWheight = u.netWheight;
          }
          if (u.grossWheight !== undefined) {
            row.sparePart.grossWheight = u.grossWheight;
          }
          if (u.country !== undefined) {
            row.sparePart.country = u.country;
          }
          row.netInput = '';
          row.grossInput = '';
          row.countryInput = '';
        }

        this.multiEditPhysicalSaved = true;
        this.multiEditListRefreshPending = true;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.applyingMultiEditSave = false;
        const backendMessage = error?.error?.message || error?.error;
        this.showErrorMessage(
          typeof backendMessage === 'string' ? backendMessage : 'No se pudieron guardar los cambios.'
        );
      }
    });
  }

  private buildMultiEditPhysicalUpdates(): Array<{
    sparePartId: number;
    netWheight?: number;
    grossWheight?: number;
    country?: string;
  }> | null {
    const out: Array<{
      sparePartId: number;
      netWheight?: number;
      grossWheight?: number;
      country?: string;
    }> = [];

    for (const row of this.multiEditRows) {
      const id = row.sparePart.sparePartId;
      const netTrim = (row.netInput || '').trim().replace(/\s/g, '').replace(',', '.');
      const grossTrim = (row.grossInput || '').trim().replace(/\s/g, '').replace(',', '.');
      const countryTrim = (row.countryInput || '').trim();

      const item: { sparePartId: number; netWheight?: number; grossWheight?: number; country?: string } = {
        sparePartId: id
      };
      let any = false;

      if (netTrim !== '') {
        const n = Number(netTrim);
        if (Number.isNaN(n)) {
          this.showErrorMessage(`Peso líquido inválido en ${this.multiEditPieceDisplayName(row)}`);
          return null;
        }
        item.netWheight = n;
        any = true;
      }

      if (grossTrim !== '') {
        const n = Number(grossTrim);
        if (Number.isNaN(n)) {
          this.showErrorMessage(`Peso bruto inválido en ${this.multiEditPieceDisplayName(row)}`);
          return null;
        }
        item.grossWheight = n;
        any = true;
      }

      if (countryTrim !== '') {
        item.country = countryTrim;
        any = true;
      }

      if (any) {
        out.push(item);
      }
    }

    return out;
  }

  private sparePartHasPackageAssigned(sp: SparePartModel): boolean {
    const raw = sp.packageNumber ?? (sp as any).PackageNumber;
    return typeof raw === 'string' && raw.trim().length > 0;
  }

  private parsePackageGroupInput(row: MultiEditSparePartRow): number | null {
    const raw = (row.packageGroupInput || '').trim();
    if (!raw) return null;
    if (!/^\d+$/.test(raw)) return null;
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) return null;
    return n;
  }

  startGeneratePackagesFromMultiEdit(): void {
    if (!this.multiEditPhysicalSaved) {
      this.showErrorMessage('Primero guardá los cambios con "Guardar edición".');
      return;
    }
    if (this.generatingPackagesFromMultiEdit) {
      return;
    }

    const alreadyPackagedRows = this.multiEditRows.filter(row =>
      this.sparePartHasPackageAssigned(row.sparePart)
    );
    if (alreadyPackagedRows.length > 0) {
      const maxShow = 8;
      const parts = alreadyPackagedRows.slice(0, maxShow).map(row => {
        const name = this.multiEditPieceDisplayName(row);
        const pkg = (row.sparePart.packageNumber || (row.sparePart as any).PackageNumber || '').trim();
        return `${name} (${pkg})`;
      });
      const suffix =
        alreadyPackagedRows.length > maxShow
          ? ` …y ${alreadyPackagedRows.length - maxShow} más.`
          : '';
      this.showErrorMessage(
        `No se pueden crear paquetes nuevos: ${alreadyPackagedRows.length} repuesto(s) ya tienen número de paquete. ${parts.join('; ')}${suffix}`
      );
      return;
    }

    const skippedLabels: string[] = [];
    const groupMap = new Map<string, { orderId: number; packageGroup: number; rows: MultiEditSparePartRow[] }>();

    for (const row of this.multiEditRows) {
      const packageGroup = this.parsePackageGroupInput(row);
      if (packageGroup == null) {
        skippedLabels.push(this.multiEditPieceDisplayName(row));
        continue;
      }

      const oid = row.sparePart.orderId;
      if (oid == null || !Number.isFinite(oid)) {
        skippedLabels.push(this.multiEditPieceDisplayName(row));
        continue;
      }

      const key = `${oid}::${packageGroup}`;
      let g = groupMap.get(key);
      if (!g) {
        g = { orderId: oid, packageGroup, rows: [] };
        groupMap.set(key, g);
      }
      g.rows.push(row);
    }

    if (groupMap.size === 0) {
      const extra =
        skippedLabels.length > 0
          ? ` Hay ${skippedLabels.length} repuesto(s) sin grupo de paquete válido u orden.`
          : '';
      this.showErrorMessage(`No hay repuestos elegibles para armar paquetes.${extra}`);
      return;
    }

    const groups = Array.from(groupMap.values());
    for (const group of groups) {
      const exportNumbers = Array.from(
        new Set(
          group.rows
            .map(row => (row.sparePart.exportNumber || '').trim())
            .filter(v => v !== '')
        )
      );

      if (exportNumbers.length === 0) {
        this.showErrorMessage(
          `El grupo ${group.packageGroup} (orden ${group.orderId}) no tiene número de exportación en sus repuestos.`
        );
        return;
      }

      if (exportNumbers.length > 1) {
        this.showErrorMessage(
          `El grupo ${group.packageGroup} (orden ${group.orderId}) mezcla múltiples números de exportación (${exportNumbers.join(', ')}).`
        );
        return;
      }
    }

    this.generatingPackagesFromMultiEdit = true;

    from(groups)
      .pipe(
        concatMap(g =>
          this.orderService.getNextPackageNumber((g.rows[0].sparePart.exportNumber || '').trim()).pipe(
            map(res => ({
              orderId: g.orderId,
              packageGroup: g.packageGroup,
              exportNumber: (g.rows[0].sparePart.exportNumber || '').trim(),
              packageNumber: res.nextPackageNumber,
              rows: g.rows
            }))
          )
        ),
        toArray(),
        finalize(() => {
          this.generatingPackagesFromMultiEdit = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (resolved: ResolvedPackageGroup[]) => {
          let msg = 'Se crearán los siguientes paquetes:\n\n';
          for (const g of resolved) {
            msg += `• Grupo ${g.packageGroup}: ${g.packageNumber} — export. ${g.exportNumber}, orden id ${g.orderId}, ${g.rows.length} repuesto(s)\n`;
          }
          if (skippedLabels.length > 0) {
            const preview = skippedLabels.slice(0, 8).join(', ');
            msg += `\nSin paquete (${skippedLabels.length}): ${preview}${skippedLabels.length > 8 ? '…' : ''}\n`;
          }
          msg += '\n¿Confirmar creación de paquetes?';
          if (!confirm(msg)) {
            return;
          }
          this.executeGeneratePackagesFromMultiEdit(resolved);
        },
        error: (error) => {
          console.error('Error al obtener números de paquete:', error);
          const backendMessage = error?.error?.message || error?.message;
          this.showErrorMessage(
            typeof backendMessage === 'string'
              ? backendMessage
              : 'Error al solicitar números de paquete.'
          );
        }
      });
  }

  private executeGeneratePackagesFromMultiEdit(resolved: ResolvedPackageGroup[]): void {
    const byOrder = new Map<number, SparePartForLabel[]>();
    for (const g of resolved) {
      for (const row of g.rows) {
        const sp = row.sparePart;
        if (!byOrder.has(g.orderId)) {
          byOrder.set(g.orderId, []);
        }
        byOrder.get(g.orderId)!.push({
          sparePartId: sp.sparePartId,
          sparePartName: sp.sparePartName || '',
          sparePartNameEs: sp.sparePartNameEs || sp.sparePartName || '',
          productCode: sp.productCode || '',
          packageNumber: g.packageNumber
        });
      }
    }

    this.generatingPackagesFromMultiEdit = true;
    from(Array.from(byOrder.entries()))
      .pipe(
        concatMap(([orderId, parts]) =>
          this.labelsService.generateLabelsAndPackage({
            orderId,
            selectedSpareParts: parts,
            generatedBy: 'spare-part-history-multi-edit'
          })
        ),
        toArray(),
        finalize(() => {
          this.generatingPackagesFromMultiEdit = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: responses => {
          const failed = responses.find(r => !r.success);
          if (failed) {
            this.showErrorMessage(failed.message || 'Error al crear paquetes en una de las órdenes.');
            return;
          }
          this.showProgressMessage(
            `Paquetes creados correctamente (${responses.length} llamada(s) a órdenes).`
          );
          this.multiEditListRefreshPending = true;
          this.closeMultiEditSparePartsModal();
        },
        error: (error) => {
          console.error('Error al generar paquetes:', error);
          const backendMessage = error?.error?.message || error?.message;
          this.showErrorMessage(
            typeof backendMessage === 'string' ? backendMessage : 'Error al generar paquetes.'
          );
        }
      });
  }

  toggleExportMenu(): void {
    this.exportMenuOpen = !this.exportMenuOpen;
  }

  copyFilteredSparePartsForWhatsapp(): void {
    if (this.isCopyingWhatsapp) return;

    this.isCopyingWhatsapp = true;
    const source$ = this.isSelectingAllFiltered
      ? this.fetchAllFilteredSpareParts().pipe(
          map(all =>
            this.hasActiveColumnFilters() ? all.filter(sp => this.passesColumnFilters(sp)) : all
          ),
          map(all => this.excludeDeselected(all))
        )
      : of(this.selectedItems.length > 0 ? [...this.selectedItems] : this.displayedSpareParts.filter(item => item['selected']));

    source$
      .pipe(
        finalize(() => {
          this.isCopyingWhatsapp = false;
        })
      )
      .subscribe({
        next: (selectedSpareParts) => {
          const distinctOrderIds = Array.from(
            new Set(
              selectedSpareParts
                .map(sp => sp.orderId)
                .filter((id): id is number => Number.isFinite(id))
            )
          );

          const continueWithOrders = (relatedOrders: OrderOutModel[]) => {
            const text = this.buildWhatsappTextFromSelectedSpareParts(selectedSpareParts, relatedOrders || []);

            if (!text.trim()) {
              this.showErrorMessage('No hay datos para copiar');
              return;
            }

            this.copyTextToClipboardSafe(text)
              .then(() => this.showProgressMessage('Datos copiados al portapapeles para WhatsApp'))
              .catch((error) => {
                console.error('Error al copiar al portapapeles:', error);
                this.showErrorMessage('No se pudieron copiar los datos');
              });
          };

          if (distinctOrderIds.length === 0) {
            continueWithOrders([]);
            return;
          }

          this.orderService.getOrdersByIds(distinctOrderIds).subscribe({
            next: (relatedOrders) => continueWithOrders(relatedOrders || []),
            error: (ordersError) => {
              console.error('No se pudieron cargar órdenes por IDs para copiar a WhatsApp:', ordersError);
              continueWithOrders([]);
            }
          });
        },
        error: (error) => {
          console.error('Error al obtener repuestos seleccionados para copiar:', error);
          this.showErrorMessage('Error al obtener los datos seleccionados');
        }
      });
  }

  private buildWhatsappTextFromSelectedSpareParts(
    selectedSpareParts: SparePartModel[],
    relatedOrders: OrderOutModel[]
  ): string {
    const orderMap = new Map<number, OrderOutModel>();
    relatedOrders.forEach(order => {
      if (Number.isFinite(order?.orderId)) {
        orderMap.set(order.orderId, order);
      }
    });

    const lines: string[] = [];
    const groupedByOrder = new Map<string, Array<{ sparePart: SparePartModel; order?: OrderOutModel }>>();

    selectedSpareParts.forEach(sparePart => {
      const order = Number.isFinite(sparePart.orderId as number)
        ? orderMap.get(sparePart.orderId as number)
        : undefined;
      const groupKey = `${order?.orderId || sparePart.orderId || 'NO_ORDER'}|${order?.orderNumber || sparePart.orderNumber || 'N/A'}|${order?.siniestro || sparePart.siniestro || 'N/A'}`;
      const current = groupedByOrder.get(groupKey) || [];
      current.push({ sparePart, order });
      groupedByOrder.set(groupKey, current);
    });

    lines.push('LISTADO ORDENES SELECCIONADAS');
    lines.push(`Total ordenes: ${groupedByOrder.size}`);
    lines.push('');

    if (groupedByOrder.size === 0) {
      lines.push('Sin resultados');
      return lines.join('\n');
    }

    groupedByOrder.forEach((items) => {
      const first = items[0];
      const siniestro = this.getWhatsappSafeValue(first?.order?.siniestro || first?.sparePart?.siniestro);
      const orderNumber = this.getWhatsappSafeValue(first?.order?.orderNumber || first?.sparePart?.orderNumber);

      items.forEach((item, index) => {
        const part = item.sparePart;
        const sparePartNameBr = this.getWhatsappSafeValue(
          part?.sparePartNamePt || part?.sparePartName || part?.sparePartNameEs
        );
        const sparePartCode = this.getWhatsappSafeValue(part?.productCode || part?.codBr);
        const packageNumber = this.getWhatsappSafeValue(part?.packageNumber);

        lines.push(
          `Siniestro: ${siniestro} | Orden: ${orderNumber} | Repuesto ${index + 1}: ${sparePartNameBr} | Codigo pieza: ${sparePartCode} | Nro paquete: ${packageNumber}`
        );
      });
    });

    return lines.join('\n');
  }

  private getWhatsappSafeValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    const text = String(value).trim();
    return text.length > 0 ? text : 'N/A';
  }

  private async copyTextToClipboardSafe(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  }

  exportOrdersFormatToExcel(): void {
    this.exportMenuOpen = false;
    this.exportToExcel();
  }

  exportTableFormatToExcel(): void {
    this.exportMenuOpen = false;
    this.exportTableColumnsToExcel();
  }

  sparePartModal(sparePart: SparePartModel): void {
    // Si el repuesto no tiene orderId, buscarlo en las órdenes
    if (!sparePart['orderId'] && this.orders && this.orders.length > 0) {
      const foundOrder = this.orders.find(order =>
        order.spareParts && order.spareParts.some(sp => sp.sparePartId === sparePart.sparePartId)
      );
      if (foundOrder) {
        sparePart['orderId'] = foundOrder.orderId;
      }
    }
    this.selectedSparePart = sparePart;
    this.activeFocusedRowId = sparePart.sparePartId;
    this.duplicateSparePartTargetOrderNumber = sparePart.orderNumber || '';
    this.isDuplicatingSparePart = false;
    this.openSparePartModal = true;
  }

  closeSparePartModal(): void {
    // Guardar la posición actual del scroll antes de cerrar el modal
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;

    this.openSparePartModal = false;
    this.selectedSparePart = null;
    this.isDuplicatingSparePart = false;
    this.duplicateSparePartTargetOrderNumber = '';

    // Restaurar la posición del scroll después de cerrar el modal
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'auto' // Usar 'auto' en lugar de 'smooth' para evitar animaciones visibles
      });
    }, 100);

    // No recargar la página actual, ya que actualizamos individualmente los repuestos
  }

  private nextTemporarySparePartId(): number {
    return this.temporarySparePartIdSeed--;
  }

  private createSparePartDuplicateDraft(source: SparePartModel): SparePartModel {
    const canonicalSource = toCanonicalSparePartKeys(source as Record<string, unknown>);
    return applyPostEmergentCommentDuplicateDefaults({
      ...canonicalSource,
      sparePartId: this.nextTemporarySparePartId(),
      receptionStatus: 'NO-LLEGO',
      providerName: '',
      taxNote: '',
      comments: '',
      netWheight: null,
      grossWheight: null,
      country: '',
      isFant: false,
      fantUrgency: null,
      recompra: false,
      control: false,
      anulado: false,
      trello: false,
      devZureo: false,
      entradaSage: false,
      controleSeba: false,
      okAnulacao: false,
      facturado: false,
      packageNumber: '',
      exportNumber: '',
      externo: '',
      interno: '',
      color: '',
      fechaDeComunicado: null,
      shippingReceipt: '',
      transportType: '',
      lastExportedAt: undefined,
      arrivalDate: undefined,
      fechaExportacion: undefined,
      shippingStatus: 'NO-ENVIADO',
      productStatus: 'CREADO'
    });
  }

  duplicateSparePartWithChanges(sparePart?: SparePartModel): void {
    const base = sparePart ?? this.selectedSparePart;
    if (!base) return;

    if (!base.orderId && this.orders?.length) {
      const foundOrder = this.orders.find(order =>
        order.spareParts && order.spareParts.some(sp => sp.sparePartId === base.sparePartId)
      );
      if (foundOrder) {
        base.orderId = foundOrder.orderId;
      }
    }

    if (!base.orderId) {
      this.showErrorMessage('No se pudo determinar la orden del repuesto a duplicar.');
      return;
    }

    this.selectedSparePart = this.createSparePartDuplicateDraft(base);
    this.activeFocusedRowId = base.sparePartId;
    this.duplicateSparePartTargetOrderNumber = base.orderNumber || '';
    this.isDuplicatingSparePart = true;
    this.openSparePartModal = true;
  }

  clearCheckbox(): void {
    this.clearSparePartsTableSelectionState();
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5; // Número máximo de páginas a mostrar

    if (this.totalPages <= maxVisiblePages) {
      // Si hay pocas páginas, mostrar todas
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Si hay muchas páginas, mostrar un subconjunto
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = startPage + maxVisiblePages - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  onPageSizeChange(): void {
    this.pageSize = Number(this.pageSize); // Asegurar que sea un número
    this.currentPage = 1; // Volver a la primera página
    this.invalidateSparePartsCache();
    this.loadSparePartsPage(this.currentPage);
  }

  // Método para obtener la fecha de inicio como Date
  getStartDate(): Date | undefined {
    return undefined;
  }

  getEndDate(): Date | undefined {
    return undefined;
  }

  /**
   * Método para aplicar la búsqueda al hacer clic en el botón o presionar Enter
   */
  applySearch(): void {
    this.isFiltering = !!(this.searchTerm && this.searchTerm.trim() !== '');

    // Si estábamos en la página 2 o superior, volvemos a la página 1
    if (this.currentPage > 1) {
      this.currentPage = 1;
    }

    // Cargar la página actual con el nuevo término de búsqueda
    this.invalidateSparePartsCache();
    this.loadSparePartsPage(this.currentPage);

    // Actualizar los totales con los nuevos filtros
    this.updateTotals();
  }

  /**
   * Método para limpiar la búsqueda
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.isFiltering = false;
    this.applySearch();
  }

  /**
   * Calcula el margen bruto (valor de venta total - costo total) de los repuestos filtrados
   * Este valor refleja únicamente los repuestos filtrados actualmente visibles
   * @returns Margen bruto de los repuestos filtrados
   */
  getGrossMargin(): number {
    return this.totalSalesValue - this.totalCost;
  }

  /**
   * Calcula el porcentaje de margen bruto de los repuestos filtrados
   * Este valor refleja únicamente los repuestos filtrados actualmente visibles
   * @returns Porcentaje de margen bruto de los repuestos filtrados
   */
  getGrossMarginPercentage(): number {
    if (this.totalCost === 0) return 0;

    return (this.getGrossMargin() / this.totalCost) * 100;
  }

  /**
   * Método para recargar los datos de repuestos
   * Útil para refrescar la lista manualmente
   */
  reloadData(): void {
    this.loading = true;
    this.currentPage = 1;

    // Recargar los repuestos con los filtros actuales
    this.invalidateSparePartsCache();
    this.loadSparePartsPage(1);

    // Actualizar los totales
    this.updateTotals();
  }

  /**
   * Método para resaltar visualmente un repuesto recién editado
   * @param sparePartId - ID del repuesto editado
   */
  highlightLastEditedSparePart(sparePartId: number): void {
    this.lastEditedSparePartId = sparePartId;

    // Quitar el resaltado después de 3 segundos
    setTimeout(() => {
      this.lastEditedSparePartId = null;
    }, 3000);
  }

  /**
   * Maneja la actualización de un repuesto específico sin recargar toda la tabla
   * @param updatedSparePart El repuesto actualizado
   */
  onSparePartUpdated(updatedSparePart: SparePartModel): void {
    const withFallbackDate = (row: SparePartModel): SparePartModel => {
      const prevStatus = (row.productStatus || '').trim().toUpperCase();
      const nextStatus = (updatedSparePart.productStatus || '').trim().toUpperCase();
      const shouldSetNow =
        prevStatus !== 'EXPORTADO' &&
        nextStatus === 'EXPORTADO' &&
        !updatedSparePart.lastExportedAt &&
        !row.lastExportedAt;

      return shouldSetNow
        ? { ...updatedSparePart, lastExportedAt: new Date() }
        : updatedSparePart;
    };

    const index = this.displayedSpareParts.findIndex(sp => Number(sp.sparePartId) === Number(updatedSparePart.sparePartId));
    if (index !== -1) {
      this.displayedSpareParts[index] = withFallbackDate(this.displayedSpareParts[index]);
      this.lastEditedSparePartId = updatedSparePart.sparePartId;
      this.displayedSpareParts = [...this.displayedSpareParts];
    }

    const filteredIndex = this.filteredSpareParts.findIndex(sp => Number(sp.sparePartId) === Number(updatedSparePart.sparePartId));
    if (filteredIndex !== -1) {
      this.filteredSpareParts[filteredIndex] = withFallbackDate(this.filteredSpareParts[filteredIndex]);
      this.filteredSpareParts = [...this.filteredSpareParts];
    }

    if (index === -1 && filteredIndex === -1) {
      this.invalidateSparePartsCache();
      this.reloadData();
      return;
    }

    this.cdr.detectChanges();
    this.closeSparePartModal();
  }

  getAllSpareParts(): SparePartModel[] {
    const allSpareParts: SparePartModel[] = [];
    this.orders.forEach((order: OrderOutModel) => {
      if (order.spareParts) {
        order.spareParts.forEach((sparePart: SparePartModel) => {
          allSpareParts.push({
            ...sparePart,
            orderNumber: order.orderNumber,
            orderId: order.orderId
          });
        });
      }
    });
    return allSpareParts;
  }

  deleteSparePart(sparePart: SparePartModel): void {
    const orderId = sparePart['orderId'];
    if (!orderId) {
      alert('No se pudo determinar la orden asociada a este repuesto. No se puede eliminar.');
      return;
    }
    if (sparePartHasPackageOrPrintedLabel(sparePart)) {
      this.showErrorMessage(SPARE_PART_DELETE_BLOCKED_MESSAGE);
      return;
    }
    if (confirm('¿Estás seguro de que deseas eliminar este repuesto?')) {
      this.sparePartService.deleteSparePart(sparePart.sparePartId, orderId).subscribe(
        () => {
          this.invalidateSparePartsCache();
          // Eliminar el repuesto de la lista
          this.displayedSpareParts = this.displayedSpareParts.filter(sp => sp.sparePartId !== sparePart.sparePartId);
          this.filteredSpareParts = this.filteredSpareParts.filter(sp => sp.sparePartId !== sparePart.sparePartId);
          this.totalSpareParts--;
          this.filteredTotal--;
        },
        error => {
          console.error('Error al eliminar el repuesto:', error);
          this.showErrorMessage(readApiErrorMessage(error, 'Error al eliminar el repuesto. Por favor, intente nuevamente.'));
        }
      );
    }
  }

  onSparePartDeleted(sparePart: SparePartModel): void {
    this.deleteSparePart(sparePart);
  }

  private calculateTotalsFromList(spareParts: SparePartModel[]): void {
    this.totalCost = spareParts.reduce((sum, part) => {
      const unitPrice = this.normalizeNumericValue(part.unitPrice);
      const amount = part.amount || 1;
      return sum + (unitPrice * amount);
    }, 0);

    this.totalSalesValue = spareParts.reduce((sum, part) => {
      const salesValue = this.normalizeNumericValue(part.valorDeVenta);
      const amount = part.amount || 1;
      return sum + (salesValue * amount);
    }, 0);
  }

  // Nuevo método para actualizar los totales
  private updateTotals(): void {
    if (this.filteredTotal === 0) {
      this.totalCost = 0;
      this.totalSalesValue = 0;
      return;
    }

    if (!Number.isFinite(this.totalCost) || !Number.isFinite(this.totalSalesValue)) {
      this.calculateTotalsFromList(this.displayedSpareParts);
    }
  }

  private getActiveColumnFiltersCount(): number {
    return Object.values(this.columnFilters).filter(value =>
      Array.isArray(value) ? value.length > 0 : !!String(value || '').trim()
    ).length;
  }

  hasAppliedFiltersForSummary(): boolean {
    return !!(this.searchTerm?.trim()) || this.onlyLast60Days || this.onlyLowSaleRatio || this.getActiveColumnFiltersCount() > 0;
  }

  /** Cantidad de repuestos anulados dentro del conjunto filtrado actual (cliente). */
  getFilteredAnnulledSparePartsCount(): number {
    return this.filteredSpareParts.filter(sp => this.isAnulado(sp)).length;
  }

  getAppliedFiltersSummary(): string {
    const parts: string[] = [];
    if (this.searchTerm?.trim()) {
      parts.push(`Busqueda: "${this.searchTerm.trim()}"`);
    }
    if (this.onlyLast60Days) {
      parts.push('Solo ultimos 60 dias');
    }
    if (this.onlyLowSaleRatio) {
      parts.push('Solo coeficiente <= 11');
    }
    const columnFiltersCount = this.getActiveColumnFiltersCount();
    if (columnFiltersCount > 0) {
      parts.push(`Filtros de columna: ${columnFiltersCount}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'Sin filtros aplicados';
  }

  // Métodos para las clases de estado responsive
  getStatusClass(status: string): string {
    if (!status) return '';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('creado') || statusLower.includes('created')) return 'created';
    if (statusLower.includes('proceso') || statusLower.includes('processing')) return 'processing';
    if (statusLower.includes('finalizado') || statusLower.includes('completed') || statusLower.includes('completado')) return 'completed';
    if (statusLower.includes('cancelado') || statusLower.includes('cancelled')) return 'cancelled';
    if (statusLower.includes('pendiente') || statusLower.includes('pending')) return 'processing';
    if (statusLower.includes('llegó') || statusLower.includes('arrived')) return 'completed';
    if (statusLower.includes('atención') || statusLower.includes('attention')) return 'cancelled';

    return 'processing'; // Estado por defecto
  }

  getShippingStatusClass(status: string): string {
    if (!status) return 'no-status';

    const statusLower = status.toLowerCase();

    // Estados enviados
    if (statusLower.includes('enviado') || statusLower.includes('shipped') || statusLower.includes('sent')) {
      return 'completed';
    }

    // Estado NO-ENVIADO (específico del sistema)
    if (statusLower === 'no-enviado' || statusLower === 'no enviado' || statusLower.includes('no-enviado')) {
      return 'no-sent';
    }

    // Estados pendientes
    if (statusLower.includes('pendiente') || statusLower.includes('pending')) {
      return 'processing';
    }

    // Estados entregados
    if (statusLower.includes('entregado') || statusLower.includes('delivered')) {
      return 'completed';
    }

    // Estados cancelados
    if (statusLower.includes('cancelado') || statusLower.includes('cancelled')) {
      return 'cancelled';
    }

    return 'processing'; // Estado por defecto
  }

  // Método helper para obtener imagen de camioneta
  getCamionetaIcon(): SafeHtml {
    const imgHtml = `<img src="/assets/images/pickup-car.png" alt="Camioneta" style="width: 24px; height: 24px; display: inline-block; vertical-align: middle;" />`;
    return this.sanitizer.bypassSecurityTrustHtml(imgHtml);
  }

  // Método para obtener el icono según el transportType del repuesto
  getTransportIcon(sparePart: SparePartModel): SafeHtml | string {
    const transportType = sparePart.transportType || (sparePart as any).TransportType;

    if (!transportType) {
      return '';
    }

    const transportLower = transportType.toLowerCase();

    if (transportLower === 'camioneta') {
      return this.getCamionetaIcon(); // Imagen de camioneta verde
    } else if (transportLower === 'camion') {
      return '🚛'; // Camión
    }

    return '';
  }

  // Método para obtener el texto descriptivo del transporte
  getTransportText(sparePart: SparePartModel): string {
    const transportType = sparePart.transportType || (sparePart as any).TransportType;

    if (!transportType) {
      return 'No cargado';
    }

    const transportLower = transportType.toLowerCase();

    if (transportLower === 'camioneta') {
      return 'Cargado en camioneta';
    } else if (transportLower === 'camion') {
      return 'Cargado en camión';
    }

    return 'Cargado';
  }

  // Método para verificar si el repuesto está cargado
  isSparePartLoaded(sparePart: SparePartModel): boolean {
    const transportType = sparePart.transportType || (sparePart as any).TransportType;
    return !!transportType && transportType.trim() !== '';
  }

  /**
   * Días calendario entre dos instantes, usando la fecha **local** (alineado con pipe dd/MM/yyyy).
   * No mezclar getFullYear/getMonth/getDate (local) con Date.UTC (eso desfasa ~1 día por TZ y puede dar valores erróneos).
   */
  private calendarDaysBetweenLocalDates(start: Date, end: Date): number {
    const a = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const b = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
  }

  /**
   * Días de plazo del repuesto: días calendario entre emisión de la orden (Data Ordem) y F. Esp. del repuesto.
   * Sin fechas válidas devuelve null.
   */
  calculateDiasPrazo(sparePart: SparePartModel): number | null {
    const emission = sparePart.orderDate != null ? new Date(sparePart.orderDate) : null;
    const arrival = sparePart.expectedArrivalDate != null ? new Date(sparePart.expectedArrivalDate) : null;
    if (!emission || !arrival || isNaN(emission.getTime()) || isNaN(arrival.getTime())) {
      return null;
    }
    return this.calendarDaysBetweenLocalDates(emission, arrival);
  }

  /** Texto para mostrar días de plazo; vacío si no hay datos. */
  formatDiasPrazo(sparePart: SparePartModel): string {
    const n = this.calculateDiasPrazo(sparePart);
    return n === null || n === undefined ? '' : String(n);
  }

  isLowSaleRatioRow(sparePart: SparePartModel): boolean {
    const rawUnitPrice = sparePart.unitPrice ?? (sparePart as any).UnitPrice;
    const rawSaleValue = sparePart.valorDeVenta ?? (sparePart as any).ValorDeVenta;
    const unitPrice = this.normalizeNumericValue(rawUnitPrice);
    const saleValue = this.normalizeNumericValue(rawSaleValue);

    if (unitPrice < 0) {
      return false;
    }
    if (unitPrice === 0) {
      return true;
    }

    return (saleValue / unitPrice) <= 11;
  }

  /** Altura aproximada del popup de filtro para calcular posición dentro del viewport */
  private readonly FILTER_POPUP_HEIGHT = 380;
  private readonly FILTER_POPUP_WIDTH = 320;
  private readonly FILTER_POPUP_MARGIN = 8;
  
  private isEmptyColumnValue(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    return String(value).trim() === '';
  }

  /**
   * Abre el menú de filtros estilo Excel para una columna (igual que órdenes, con multi-selección).
   * Posiciona el popup siempre dentro del viewport para que no se corte.
   */
  openFilterMenu(column: string, event: Event): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    let x = rect.left;
    let y = rect.bottom + 5;

    // Si no cabe abajo, mostrar arriba del disparador
    if (y + this.FILTER_POPUP_HEIGHT > window.innerHeight - this.FILTER_POPUP_MARGIN) {
      y = rect.top - this.FILTER_POPUP_HEIGHT - 5;
    }
    if (y < this.FILTER_POPUP_MARGIN) {
      y = this.FILTER_POPUP_MARGIN;
    }

    if (x + this.FILTER_POPUP_WIDTH > window.innerWidth - this.FILTER_POPUP_MARGIN) {
      x = window.innerWidth - this.FILTER_POPUP_WIDTH - this.FILTER_POPUP_MARGIN;
    }
    if (x < this.FILTER_POPUP_MARGIN) {
      x = this.FILTER_POPUP_MARGIN;
    }

    this.filterMenuPosition = { x, y };
    // Altura máxima para que el popup no se corte: espacio desde y hasta el borde del viewport
    this.filterMenuMaxHeight = window.innerHeight - y - this.FILTER_POPUP_MARGIN;

    this.activeFilterMenu = this.activeFilterMenu === column ? null : column;
    this.filterSearchTerm = '';

    if (this.activeFilterMenu === column) {
      // Inicializar selección temporal desde el filtro actual (permite Cancelar sin aplicar)
      const current = this.columnFilters[column];
      this.tempColumnFilterSelection = Array.isArray(current)
        ? [...current]
        : (current != null && current !== '' ? [String(current)] : []);
      this.loadUniqueValuesForColumn(column);
    }
  }

  /** Cierra el popup de filtro: apply=true aplica la selección temporal; apply=false cancela. */
  closeColumnFilterMenu(apply: boolean): void {
    if (apply && this.activeFilterMenu) {
      this.columnFilters[this.activeFilterMenu] =
        this.tempColumnFilterSelection.length > 0 ? [...this.tempColumnFilterSelection] : [];
      this.applyColumnFilters();
    }
    this.activeFilterMenu = null;
    this.filterSearchTerm = '';
    this.tempColumnFilterSelection = [];
  }

  /** Limpia la selección temporal dentro del popup (solo al pulsar Aplicar se persiste). */
  clearTempColumnFilter(): void {
    this.tempColumnFilterSelection = [];
  }

  /** Limpiar el filtro de la columna actual y aplicar al instante (refresco automático). */
  clearAndApplyColumnFilter(): void {
    this.tempColumnFilterSelection = [];
    this.closeColumnFilterMenu(true);
  }

  /**
   * Carga valores únicos para una columna específica
   */
  loadUniqueValuesForColumn(column: string): void {
    this.loadingUniqueValues = true;
    this.sparePartService.getDistinctColumnValues(
      column,
      this.searchTerm,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getDistinctColumnFiltersExcept(column),
      this.onlyLast60Days ? 60 : undefined,
      this.onlyLowSaleRatio
    ).pipe(
      finalize(() => {
        this.loadingUniqueValues = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (response) => {
        const values = Array.isArray(response?.values) ? [...response.values] : [];
        values.sort((a, b) => a.localeCompare(b));
        if (response?.hasEmpty) {
          values.unshift(this.EMPTY_FILTER_VALUE);
        }

        this.uniqueColumnValues[column] = values;

        switch (column) {
          case 'exportNumber':
            this.uniqueExportNumbers = values;
            break;
          case 'fornecedor':
            this.uniqueFornecedores = values;
            break;
          case 'vendedor':
            this.uniqueVendedores = values;
            break;
          case 'productStatus':
          case 'estado':
            this.uniqueEstados = values;
            break;
          case 'shippingStatus':
          case 'estadoEnvio':
            this.uniqueEstadoEnvio = values;
            break;
          case 'country':
          case 'país':
            this.uniquePaises = values;
            break;
        }
      },
      error: (error) => {
        console.error('Error al cargar valores únicos:', error);
      }
    });
  }

  private getDistinctColumnFiltersExcept(exceptColumn?: string): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};
    Object.entries(this.columnFilters || {}).forEach(([key, value]) => {
      if (exceptColumn && key.toLowerCase() === exceptColumn.toLowerCase()) {
        return;
      }

      if (Array.isArray(value)) {
        const selected = value
          .map(v => String(v ?? '').trim())
          .filter(v => v !== '');
        if (selected.length > 0) {
          normalized[key] = selected;
        }
      } else if (value !== null && value !== undefined) {
        const raw = String(value).trim();
        if (raw !== '') {
          normalized[key] = [raw];
        }
      }
    });
    return normalized;
  }

  private getSparePartColumnDisplayValue(sparePart: SparePartModel, column: string): string {
    let value: any = null;
    switch (column) {
      case 'transportType':
      case 'carregamento':
        value = this.getTransportText(sparePart);
        break;
      case 'fornecedor':
        value = sparePart.fornecedor;
        break;
      case 'vendedor':
        value = sparePart.vendedor;
        break;
      case 'productStatus':
      case 'estado':
        value = sparePart.productStatus;
        break;
      case 'isFant':
        value = this.getBooleanFlagLabel(sparePart.isFant ?? (sparePart as any).IsFant);
        break;
      case 'fantUrgency':
        value = this.getFantUrgencyLabel(sparePart);
        break;
      case 'needReview':
        value = this.getBooleanFlagLabel(this.isNeedReview(sparePart));
        break;
      case 'recompra':
        value = this.getRecompraLabel(sparePart);
        break;
      case 'control':
        value = this.getBooleanFlagLabel(sparePart.control ?? (sparePart as any).Control);
        break;
      case 'anulado':
        value = this.getBooleanFlagLabel(sparePart.anulado ?? (sparePart as any).Anulado);
        break;
      case 'trello':
        value = this.getBooleanFlagLabel(sparePart.trello ?? (sparePart as any).Trello);
        break;
      case 'devZureo':
        value = this.getBooleanFlagLabel(sparePart.devZureo ?? (sparePart as any).DevZureo);
        break;
      case 'entradaSage':
        value = this.getBooleanFlagLabel(sparePart.entradaSage ?? (sparePart as any).EntradaSage);
        break;
      case 'controleSeba':
        value = this.getBooleanFlagLabel(sparePart.controleSeba ?? (sparePart as any).ControleSeba);
        break;
      case 'okAnulacao':
        value = this.getBooleanFlagLabel(sparePart.okAnulacao ?? (sparePart as any).OkAnulacao);
        break;
      case 'facturado':
        value = this.getBooleanFlagLabel(sparePart.facturado ?? (sparePart as any).Facturado);
        break;
      case 'sendYesOrNo':
        value = sparePart.sendYesOrNo === 'true' ? 'SI' : 'NO';
        break;
      case 'mlDeadline':
        value = sparePart.mlDeadline ? this.formatDate(sparePart.mlDeadline) : '';
        break;
      case 'lastExportedAt':
        value = sparePart.lastExportedAt ? this.formatDateTime(sparePart.lastExportedAt) : '';
        break;
      case 'fechaExportacion':
        value = sparePart.fechaExportacion ? this.formatDate(sparePart.fechaExportacion) : '';
        break;
      case 'expectedArrivalDate':
        value = sparePart.expectedArrivalDate ? this.formatDate(sparePart.expectedArrivalDate) : '';
        break;
      case 'shippingStatus':
      case 'estadoEnvio':
        value = sparePart.shippingStatus;
        break;
      case 'receptionStatus':
      case 'recepcion':
        value = this.getReceptionDisplayValue(sparePart);
        break;
      case 'country':
      case 'país':
        value = sparePart.country;
        break;
      case 'diasPrazo':
        value = this.calculateDiasPrazo(sparePart);
        break;
      case 'diasAtraso':
        value = this.getDelayDays(sparePart);
        break;
      case 'fechaDeComunicado':
        value = sparePart.fechaDeComunicado ? this.formatDate(sparePart.fechaDeComunicado) : '';
        break;
      case 'emergentComment':
        value = this.getEmergentCommentDisplay(sparePart);
        break;
      default:
        value = sparePart[column as keyof SparePartModel];
    }

    return value !== null && value !== undefined ? String(value).trim() : '';
  }

  private getClientDistinctValuesForColumn(column: string, source: SparePartModel[]): string[] {
    const values = new Set<string>();
    let hasEmpty = false;

    for (const sparePart of source) {
      const value = this.getSparePartColumnDisplayValue(sparePart, column);
      if (value !== '') {
        values.add(value);
      } else {
        hasEmpty = true;
      }
    }

    const sortedValues = Array.from(values).sort((a, b) => {
      const an = Number(a);
      const bn = Number(b);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return an - bn;
      }
      return a.localeCompare(b);
    });

    if (hasEmpty) {
      sortedValues.unshift(this.EMPTY_FILTER_VALUE);
    }
    return sortedValues;
  }

  private getClientDistinctValuesForCalculatedColumn(column: string, source: SparePartModel[]): string[] {
    const values = new Set<string>();
    let hasEmpty = false;

    for (const sparePart of source) {
      let value: number | string | null | undefined = '';
      if (column === 'diasPrazo') {
        value = this.calculateDiasPrazo(sparePart);
      } else if (column === 'diasAtraso') {
        value = this.getDelayDays(sparePart);
      }

      const normalized = value !== null && value !== undefined ? String(value).trim() : '';
      if (normalized) {
        values.add(normalized);
      } else {
        hasEmpty = true;
      }
    }

    const sortedValues = Array.from(values).sort((a, b) => Number(a) - Number(b));
    if (hasEmpty) {
      sortedValues.unshift(this.EMPTY_FILTER_VALUE);
    }
    return sortedValues;
  }

  /**
   * Aplica filtros estilo Excel
   */
  applyColumnFilters(): void {
    // Los filtros se aplican automáticamente en el método que filtra los datos
    this.currentPage = 1;
    this.invalidateSparePartsCache();
    this.loadSparePartsPage(this.currentPage);
    this.updateTotals();
  }

  /**
   * Limpia el filtro de una columna específica
   */
  clearColumnFilter(column: string): void {
    delete this.columnFilters[column];
    this.applyColumnFilters();
  }

  /** Indica si hay al menos un filtro por columna activo (para template). */
  get hasAnyColumnFilter(): boolean {
    return this.hasActiveColumnFilters();
  }

  /** Limpia todos los filtros por columna y refresca la tabla. */
  clearAllColumnFilters(): void {
    this.columnFilters = {};
    this.applyColumnFilters();
  }

  /**
   * Verifica si un repuesto pasa los filtros de columna
   */
  passesColumnFilters(sparePart: SparePartModel): boolean {
    for (const [column, filterValue] of Object.entries(this.columnFilters)) {
      if (!filterValue || filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) continue;

      let value: any = null;

      switch (column) {
        case 'transportType':
        case 'carregamento':
          value = this.getTransportText(sparePart);
          break;
        case 'fornecedor':
          value = sparePart.fornecedor;
          break;
        case 'vendedor':
          value = sparePart.vendedor;
          break;
        case 'productStatus':
        case 'estado':
          value = sparePart.productStatus;
          break;
        case 'isFant':
          value = this.getBooleanFlagLabel(sparePart.isFant ?? (sparePart as any).IsFant);
          break;
        case 'fantUrgency':
          value = this.getFantUrgencyLabel(sparePart);
          break;
        case 'needReview':
          value = this.getBooleanFlagLabel(this.isNeedReview(sparePart));
          break;
        case 'recompra':
          value = this.getRecompraLabel(sparePart);
          break;
        case 'control':
          value = this.getBooleanFlagLabel(sparePart.control ?? (sparePart as any).Control);
          break;
        case 'anulado':
          value = this.getBooleanFlagLabel(sparePart.anulado ?? (sparePart as any).Anulado);
          break;
        case 'trello':
          value = this.getBooleanFlagLabel(sparePart.trello ?? (sparePart as any).Trello);
          break;
        case 'devZureo':
          value = this.getBooleanFlagLabel(sparePart.devZureo ?? (sparePart as any).DevZureo);
          break;
        case 'entradaSage':
          value = this.getBooleanFlagLabel(sparePart.entradaSage ?? (sparePart as any).EntradaSage);
          break;
        case 'controleSeba':
          value = this.getBooleanFlagLabel(sparePart.controleSeba ?? (sparePart as any).ControleSeba);
          break;
        case 'okAnulacao':
          value = this.getBooleanFlagLabel(sparePart.okAnulacao ?? (sparePart as any).OkAnulacao);
          break;
        case 'facturado':
          value = this.getBooleanFlagLabel(sparePart.facturado ?? (sparePart as any).Facturado);
          break;
        case 'mlDeadline':
          value = sparePart.mlDeadline ? this.formatDate(sparePart.mlDeadline) : '';
          break;
        case 'lastExportedAt':
          value = sparePart.lastExportedAt ? this.formatDateTime(sparePart.lastExportedAt) : '';
          break;
        case 'fechaExportacion':
          value = sparePart.fechaExportacion ? this.formatDate(sparePart.fechaExportacion) : '';
          break;
        case 'expectedArrivalDate':
          value = sparePart.expectedArrivalDate ? this.formatDate(sparePart.expectedArrivalDate) : '';
          break;
        case 'shippingStatus':
        case 'estadoEnvio':
          value = sparePart.shippingStatus;
          break;
        case 'sendYesOrNo':
          value = sparePart.sendYesOrNo === 'true' ? 'SI' : 'NO';
          break;
        case 'receptionStatus':
        case 'recepcion':
          value = this.getReceptionDisplayValue(sparePart);
          break;
        case 'country':
        case 'país':
          value = sparePart.country;
          break;
        case 'diasPrazo':
          value = this.calculateDiasPrazo(sparePart);
          break;
        case 'diasAtraso':
          value = this.getDelayDays(sparePart);
          break;
        case 'fechaDeComunicado':
          value = sparePart.fechaDeComunicado ? this.formatDate(sparePart.fechaDeComunicado) : '';
          break;
        case 'emergentComment':
          value = this.getEmergentCommentDisplay(sparePart);
          break;
        default:
          value = sparePart[column as keyof SparePartModel];
      }

      // Si el filtro es un array (selección múltiple)
      if (Array.isArray(filterValue)) {
        const valueStr = value !== null && value !== undefined ? String(value).trim() : '';
        const includesEmpty = filterValue.includes(this.EMPTY_FILTER_VALUE) || filterValue.includes('(vazio)');
        const matchesSelectedValue = filterValue.includes(valueStr);
        const matchesEmpty = includesEmpty && valueStr === '';
        if (!matchesSelectedValue && !matchesEmpty) {
          return false;
        }
      } else {
        // Filtro simple
        const valueStr = value !== null && value !== undefined ? String(value).toLowerCase() : '';
        const filterStr = String(filterValue).toLowerCase();

        // Permitir filtrar campos vacíos
        if (filterStr === '(vacío)' || filterStr === '(vazio)') {
          if (valueStr !== '') {
            return false;
          }
        } else if (!valueStr.includes(filterStr)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Verifica si un repuesto pasa los filtros de columna excepto la columna indicada (para cascada tipo Excel).
   */
  private passesColumnFiltersExcept(sparePart: SparePartModel, exceptColumn: string): boolean {
    for (const [column, filterValue] of Object.entries(this.columnFilters)) {
      if (column === exceptColumn) continue;
      if (!filterValue || filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) continue;

      let value: any = null;
      switch (column) {
        case 'transportType':
        case 'carregamento':
          value = this.getTransportText(sparePart);
          break;
        case 'fornecedor':
          value = sparePart.fornecedor;
          break;
        case 'vendedor':
          value = sparePart.vendedor;
          break;
        case 'productStatus':
        case 'estado':
          value = sparePart.productStatus;
          break;
        case 'isFant':
          value = this.getBooleanFlagLabel(sparePart.isFant ?? (sparePart as any).IsFant);
          break;
        case 'fantUrgency':
          value = this.getFantUrgencyLabel(sparePart);
          break;
        case 'needReview':
          value = this.getBooleanFlagLabel(this.isNeedReview(sparePart));
          break;
        case 'recompra':
          value = this.getRecompraLabel(sparePart);
          break;
        case 'control':
          value = this.getBooleanFlagLabel(sparePart.control ?? (sparePart as any).Control);
          break;
        case 'anulado':
          value = this.getBooleanFlagLabel(sparePart.anulado ?? (sparePart as any).Anulado);
          break;
        case 'trello':
          value = this.getBooleanFlagLabel(sparePart.trello ?? (sparePart as any).Trello);
          break;
        case 'devZureo':
          value = this.getBooleanFlagLabel(sparePart.devZureo ?? (sparePart as any).DevZureo);
          break;
        case 'entradaSage':
          value = this.getBooleanFlagLabel(sparePart.entradaSage ?? (sparePart as any).EntradaSage);
          break;
        case 'controleSeba':
          value = this.getBooleanFlagLabel(sparePart.controleSeba ?? (sparePart as any).ControleSeba);
          break;
        case 'okAnulacao':
          value = this.getBooleanFlagLabel(sparePart.okAnulacao ?? (sparePart as any).OkAnulacao);
          break;
        case 'facturado':
          value = this.getBooleanFlagLabel(sparePart.facturado ?? (sparePart as any).Facturado);
          break;
        case 'mlDeadline':
          value = sparePart.mlDeadline ? this.formatDate(sparePart.mlDeadline) : '';
          break;
        case 'lastExportedAt':
          value = sparePart.lastExportedAt ? this.formatDateTime(sparePart.lastExportedAt) : '';
          break;
        case 'fechaExportacion':
          value = sparePart.fechaExportacion ? this.formatDate(sparePart.fechaExportacion) : '';
          break;
        case 'expectedArrivalDate':
          value = sparePart.expectedArrivalDate ? this.formatDate(sparePart.expectedArrivalDate) : '';
          break;
        case 'shippingStatus':
        case 'estadoEnvio':
          value = sparePart.shippingStatus;
          break;
        case 'sendYesOrNo':
          value = sparePart.sendYesOrNo === 'true' ? 'SI' : 'NO';
          break;
        case 'receptionStatus':
        case 'recepcion':
          value = this.getReceptionDisplayValue(sparePart);
          break;
        case 'country':
        case 'país':
          value = sparePart.country;
          break;
        case 'diasPrazo':
          value = this.calculateDiasPrazo(sparePart);
          break;
        case 'diasAtraso':
          value = this.getDelayDays(sparePart);
          break;
        case 'fechaDeComunicado':
          value = sparePart.fechaDeComunicado ? this.formatDate(sparePart.fechaDeComunicado) : '';
          break;
        case 'emergentComment':
          value = this.getEmergentCommentDisplay(sparePart);
          break;
        default:
          value = sparePart[column as keyof SparePartModel];
      }
      if (Array.isArray(filterValue)) {
        const valueStr = value !== null && value !== undefined ? String(value).trim() : '';
        const includesEmpty = filterValue.includes(this.EMPTY_FILTER_VALUE) || filterValue.includes('(vazio)');
        const matchesSelectedValue = filterValue.includes(valueStr);
        const matchesEmpty = includesEmpty && valueStr === '';
        if (!matchesSelectedValue && !matchesEmpty) {
          return false;
        }
      } else {
        const valueStr = value !== null && value !== undefined ? String(value).toLowerCase() : '';
        const filterStr = String(filterValue).toLowerCase();
        if (filterStr === '(vacío)' || filterStr === '(vazio)') {
          if (valueStr !== '') return false;
        } else if (!valueStr.includes(filterStr)) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Maneja el toggle del flag FANT para un repuesto
   */
  onFantToggled(sparePart: SparePartModel): void {
    const currentValue = !!(sparePart.isFant ?? (sparePart as any).isFant ?? (sparePart as any).IsFant);
    const previousUrgency = sparePart.fantUrgency;
    sparePart.isFant = currentValue;
    sparePart.fantUrgency = defaultFantUrgency(currentValue, null);

    this.sparePartService.updateSparePartFantFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {
        // Nada especial, el valor ya está actualizado en memoria
      },
      error: (error) => {
        console.error('Error al actualizar flag FANT:', error);
        sparePart.isFant = !currentValue;
        sparePart.fantUrgency = previousUrgency;
        this.showErrorMessage('Error al actualizar el campo FANT. Por favor, intente nuevamente.');
      }
    });
  }

  isFantChecked(sparePart: SparePartModel): boolean {
    return !!(sparePart.isFant ?? (sparePart as any).IsFant);
  }

  getFantUrgency(sparePart: SparePartModel): FantUrgency | null {
    return normalizeFantUrgency(sparePart.fantUrgency ?? (sparePart as any).FantUrgency);
  }

  getFantUrgencyLabel(sparePart: SparePartModel): string {
    const urgency = this.getFantUrgency(sparePart);
    if (urgency === 'green') return 'Verde';
    if (urgency === 'yellow') return 'Amarillo';
    if (urgency === 'red') return 'Rojo';
    return '';
  }

  setTableFantUrgency(sparePart: SparePartModel, value: string): void {
    if (!this.isFantChecked(sparePart)) return;
    const previous = this.getFantUrgency(sparePart);
    const next = normalizeFantUrgency(value);
    if (next === previous) return;
    sparePart.fantUrgency = next;

    this.sparePartService.updateSparePartFantUrgency(sparePart.sparePartId, next).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar urgencia FANT:', error);
        sparePart.fantUrgency = previous;
        this.showErrorMessage('Error al actualizar la urgencia FANT. Por favor, intente nuevamente.');
      }
    });
  }

  isRecompra(sparePart: SparePartModel): boolean {
    return !!(sparePart.recompra ?? (sparePart as any).Recompra);
  }

  isNeedReview(sparePart: SparePartModel): boolean {
    const value = sparePart.needReview ?? (sparePart as any).NeedReview;
    return value === true || value === 'true' || value === 'True' || value === 1 || value === '1';
  }

  getRecompraLabel(sparePart: SparePartModel): string {
    return this.isRecompra(sparePart) ? 'SI' : 'NO';
  }

  getBooleanFlagLabel(value: any): string {
    return value === true || value === 'true' || value === 'True' || value === 1 || value === '1' ? 'SI' : 'NO';
  }

  onRecompraToggled(sparePart: SparePartModel): void {
    const currentValue = this.isRecompra(sparePart);
    sparePart.recompra = currentValue;

    this.sparePartService.updateSparePartRecompraFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {
        // Nada especial, el valor ya está actualizado en memoria
      },
      error: (error) => {
        console.error('Error al actualizar flag Recompra:', error);
        sparePart.recompra = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Recompra. Por favor, intente nuevamente.');
      }
    });
  }

  isControl(sparePart: SparePartModel): boolean {
    return !!(sparePart.control ?? (sparePart as any).Control);
  }

  getSparePartRowClass(sparePart: SparePartModel): string {
    if (this.isAnulado(sparePart)) return 'anulado-row';
    const isFant = !!(sparePart.isFant ?? (sparePart as any).IsFant);
    return isFant ? 'fant-row' : '';
  }

  onControlToggled(sparePart: SparePartModel): void {
    const currentValue = this.isControl(sparePart);
    sparePart.control = currentValue;

    this.sparePartService.updateSparePartControlFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {
        // Valor ya sincronizado en memoria
      },
      error: (error) => {
        console.error('Error al actualizar flag Control:', error);
        sparePart.control = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Control. Por favor, intente nuevamente.');
      }
    });
  }

  isAnulado(sparePart: SparePartModel): boolean {
    return !!(sparePart.anulado ?? (sparePart as any).Anulado);
  }

  onAnuladoToggled(sparePart: SparePartModel): void {
    const currentValue = this.isAnulado(sparePart);
    sparePart.anulado = currentValue;

    this.sparePartService.updateSparePartAnuladoFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag Anulado:', error);
        sparePart.anulado = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Anulado. Por favor, intente nuevamente.');
      }
    });
  }

  isTrello(sparePart: SparePartModel): boolean {
    return !!(sparePart.trello ?? (sparePart as any).Trello);
  }

  onTrelloToggled(sparePart: SparePartModel): void {
    const currentValue = this.isTrello(sparePart);
    sparePart.trello = currentValue;

    this.sparePartService.updateSparePartTrelloFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag Trello:', error);
        sparePart.trello = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Trello. Por favor, intente nuevamente.');
      }
    });
  }

  isDevZureo(sparePart: SparePartModel): boolean {
    return !!(sparePart.devZureo ?? (sparePart as any).DevZureo);
  }

  onDevZureoToggled(sparePart: SparePartModel): void {
    const currentValue = this.isDevZureo(sparePart);
    sparePart.devZureo = currentValue;

    this.sparePartService.updateSparePartDevZureoFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag DevZureo:', error);
        sparePart.devZureo = !currentValue;
        this.showErrorMessage('Error al actualizar el campo DevZureo. Por favor, intente nuevamente.');
      }
    });
  }

  isEntradaSage(sparePart: SparePartModel): boolean {
    return !!(sparePart.entradaSage ?? (sparePart as any).EntradaSage);
  }

  onEntradaSageToggled(sparePart: SparePartModel): void {
    const currentValue = this.isEntradaSage(sparePart);
    sparePart.entradaSage = currentValue;

    this.sparePartService.updateSparePartEntradaSageFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag EntradaSage:', error);
        sparePart.entradaSage = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Entrada Sage. Por favor, intente nuevamente.');
      }
    });
  }

  isControleSeba(sparePart: SparePartModel): boolean {
    return !!(sparePart.controleSeba ?? (sparePart as any).ControleSeba);
  }

  onControleSebaToggled(sparePart: SparePartModel): void {
    const currentValue = this.isControleSeba(sparePart);
    sparePart.controleSeba = currentValue;

    this.sparePartService.updateSparePartControleSebaFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag ControleSeba:', error);
        sparePart.controleSeba = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Controle Seba. Por favor, intente nuevamente.');
      }
    });
  }

  isOkAnulacao(sparePart: SparePartModel): boolean {
    return !!(sparePart.okAnulacao ?? (sparePart as any).OkAnulacao);
  }

  onOkAnulacaoToggled(sparePart: SparePartModel): void {
    const currentValue = this.isOkAnulacao(sparePart);
    sparePart.okAnulacao = currentValue;

    this.sparePartService.updateSparePartOkAnulacaoFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag OkAnulacao:', error);
        sparePart.okAnulacao = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Ok Anulação. Por favor, intente nuevamente.');
      }
    });
  }

  isFacturado(sparePart: SparePartModel): boolean {
    return !!(sparePart.facturado ?? (sparePart as any).Facturado);
  }

  onFacturadoToggled(sparePart: SparePartModel): void {
    const currentValue = this.isFacturado(sparePart);
    sparePart.facturado = currentValue;

    this.sparePartService.updateSparePartFacturadoFlag(sparePart.sparePartId, currentValue).subscribe({
      next: () => {},
      error: (error) => {
        console.error('Error al actualizar flag Facturado:', error);
        sparePart.facturado = !currentValue;
        this.showErrorMessage('Error al actualizar el campo Facturado. Por favor, intente nuevamente.');
      }
    });
  }

  /**
   * Obtiene valores únicos filtrados por término de búsqueda
   */
  // Sin trackBy, Angular destruye y recrea todas las filas cada vez que el array cambia
  // de identidad. Las listas de entidades se siguen por su id, que es unico por venir de
  // la base. Las de strings van por indice a proposito: no hay garantia de que no repitan
  // valores, y una clave duplicada no degrada el rendimiento, hace explotar el render
  // con NG0955.
  trackBySparePartId(_index: number, sparePart: SparePartModel): number {
    return sparePart.sparePartId;
  }

  trackByMultiEditRow(_index: number, row: MultiEditSparePartRow): number {
    return row.sparePart.sparePartId;
  }

  trackByIndex(index: number): number {
    return index;
  }

  getFilteredUniqueValues(column: string): string[] {
    const cachedValues = this.uniqueColumnValues[column];
    let values: string[] = cachedValues ? [...cachedValues] : [];

    if (values.length === 0) {
      switch (column) {
        case 'exportNumber':
          values = this.uniqueExportNumbers;
          break;
        case 'fornecedor':
          values = this.uniqueFornecedores;
          break;
        case 'vendedor':
          values = this.uniqueVendedores;
          break;
        case 'productStatus':
        case 'estado':
          values = this.uniqueEstados;
          break;
        case 'shippingStatus':
        case 'estadoEnvio':
          values = this.uniqueEstadoEnvio;
          break;
        case 'country':
        case 'país':
          values = this.uniquePaises;
          break;
      }
    }

    if (this.filterSearchTerm) {
      const searchLower = this.filterSearchTerm.toLowerCase();
      return values.filter(v => v.toLowerCase().includes(searchLower));
    }

    return values;
  }

  /**
   * Verifica si un valor está seleccionado en el filtro (en popup usa selección temporal).
   */
  isValueSelected(column: string, value: string): boolean {
    if (column === this.activeFilterMenu) {
      return this.tempColumnFilterSelection.includes(value);
    }
    const filter = this.columnFilters[column];
    if (Array.isArray(filter)) {
      return filter.includes(value);
    }
    return false;
  }

  /**
   * Alterna la selección de un valor en el filtro (multi-selección; en popup modifica solo temporal).
   */
  toggleFilterValue(column: string, value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (column === this.activeFilterMenu) {
      if (checked) {
        if (!this.tempColumnFilterSelection.includes(value)) {
          this.tempColumnFilterSelection.push(value);
        }
      } else {
        const index = this.tempColumnFilterSelection.indexOf(value);
        if (index > -1) {
          this.tempColumnFilterSelection.splice(index, 1);
        }
      }
      return;
    }

    if (!this.columnFilters[column]) {
      this.columnFilters[column] = [];
    }
    if (!Array.isArray(this.columnFilters[column])) {
      this.columnFilters[column] = [];
    }
    if (checked) {
      if (!this.columnFilters[column].includes(value)) {
        this.columnFilters[column].push(value);
      }
    } else {
      const index = this.columnFilters[column].indexOf(value);
      if (index > -1) {
        this.columnFilters[column].splice(index, 1);
      }
    }
  }

  areAllVisibleFilterValuesSelected(column: string | null): boolean {
    if (!column) {
      return false;
    }

    const visibleValues = this.getFilteredUniqueValues(column);
    return visibleValues.length > 0
      && visibleValues.every(value => this.tempColumnFilterSelection.includes(value));
  }

  toggleAllVisibleFilterValues(column: string | null): void {
    if (!column) {
      return;
    }

    const visibleValues = this.getFilteredUniqueValues(column);
    if (visibleValues.length === 0) {
      return;
    }

    const allSelected = visibleValues.every(value => this.tempColumnFilterSelection.includes(value));
    if (allSelected) {
      this.tempColumnFilterSelection = this.tempColumnFilterSelection
        .filter(selected => !visibleValues.includes(selected));
      return;
    }

    const selectedValues = new Set(this.tempColumnFilterSelection);
    visibleValues.forEach(value => selectedValues.add(value));
    this.tempColumnFilterSelection = Array.from(selectedValues);
  }

  /** Quita el banner flotante usado en exportaciones Excel desde historial de repuestos. */
  private removeExcelLoadingIndicator(): void {
    document.getElementById('excel-loading-indicator')?.remove();
  }

  private countDistinctOrderIds(spareParts: SparePartModel[]): number {
    return new Set(
      spareParts
        .map(sp => sp.orderId)
        .filter((id): id is number => Number.isFinite(id))
    ).size;
  }

  /**
   * Exporta los repuestos filtrados a Excel
   */
  exportToExcel(): void {
    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'excel-loading-indicator';
    loadingIndicator.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center';
    loadingIndicator.innerHTML = `
      <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
      <span>Obteniendo repuestos para exportar...</span>
    `;
    document.body.appendChild(loadingIndicator);

    this.getSparePartsDatasetForExport().subscribe({
      next: (filteredSpareParts) => {
        if (filteredSpareParts.length === 0) {
          this.showErrorMessage('No hay datos para exportar');
          this.removeExcelLoadingIndicator();
          return;
        }

        this.removeExcelLoadingIndicator();
        const count = filteredSpareParts.length;
        const confirmMsg =
          `Se exportarán ${count} repuesto(s) (una fila por repuesto), en formato tipo orden.\n\n¿Desea continuar?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }

        loadingIndicator.querySelector('span')!.textContent = 'Generando archivo Excel...';
        document.body.appendChild(loadingIndicator);

        try {
          const distinctOrderIds = this.countDistinctOrderIds(filteredSpareParts);

          const data: any[][] = [];
          const EXCEL_COLUMNS_COUNT = 32;
          const sanitizeExcelRow = (row: any[], nullFallback: string = ''): any[] => {
            const sanitized = row.map((value) => (value === null || value === undefined ? nullFallback : value));
            if (sanitized.length < EXCEL_COLUMNS_COUNT) {
              return [...sanitized, ...Array(EXCEL_COLUMNS_COUNT - sanitized.length).fill(nullFallback)];
            }
            if (sanitized.length > EXCEL_COLUMNS_COUNT) {
              return sanitized.slice(0, EXCEL_COLUMNS_COUNT);
            }
            return sanitized;
          };

          data.push([
            'Número de Orden',
            'Fecha de Creación',
            'Siniestro',
            'Número de Factura',
            'Cliente',
            'Teléfono Cliente',
            'Proveedor',
            'Teléfono Proveedor',
            'Fecha de Llegada Espera',
            'Empleado',
            'Nombre del Repuesto',
            'Código del Producto',
            'Cantidad',
            'Precio Unitario',
            'Estado del Producto',
            'Nota Fiscal',
            'NCM',
            'Código BR',
            'Valor de Venta TOTAL',
            'Dañado',
            'Peso Neto',
            'Peso Bruto',
            'Fecha de Llegada Espera',
            'Precio Total de la Orden',
            'Cant. Rep. Orden',
            'País',
            'Vehículo',
            'Dirección de Entrega',
            'Número de Paquete',
            'Es Exportación',
            'Enviar',
            'Estado de Envío'
          ]);

          filteredSpareParts.forEach(sparePart => {
            data.push(sanitizeExcelRow([
              sparePart.orderNumber || '',
              this.formatDate(sparePart.orderDate),
              sparePart.siniestro || '',
              sparePart.invoiceNumber || '',
              sparePart.clientName || '',
              sparePart.clientPhone || '',
              sparePart.fornecedor || sparePart.providerName || '',
              sparePart.providerPhone || '',
              this.formatDate(sparePart.orderExpectedArrivalDate),
              sparePart.vendedor || '',
              sparePart.sparePartNamePt || sparePart.sparePartName || '',
              sparePart.productCode || '',
              sparePart.amount?.toString() || '',
              sparePart.unitPrice?.toString() || '',
              sparePart.productStatus || '',
              sparePart.taxNote || '',
              sparePart.ncm || '',
              sparePart.codBr || '',
              sparePart.valorDeVenta?.toString() || '',
              sparePart.isDamaged || '',
              sparePart.netWheight?.toString() || '',
              sparePart.grossWheight?.toString() || '',
              this.formatDate((sparePart as any).arrivalDate || sparePart.expectedArrivalDate),
              sparePart.orderTotalPrice?.toString() || '0',
              sparePart.orderSparePartsCount?.toString() || '0',
              sparePart.country || '',
              sparePart.carMakeModel || '',
              sparePart.deliveryAddress || '',
              sparePart.packageNumber || '',
              sparePart.isExport || '',
              sparePart.sendYesOrNo || '',
              sparePart.shippingStatus || ''
            ]));
          });

          const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
          ws['!cols'] = data[0].map(() => ({ wch: 20 }));
          const wb: XLSX.WorkBook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Órdenes');

          const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

          const date = new Date();
          const dateStr = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
          const fileName = `Ordenes_desde_repuestos_${dateStr}.xlsx`;
          XLSX.writeFile(wb, fileName);

          const timestamp = date.toISOString().replace(/[:.]/g, '-');
          this.exportedFoldersService.createBasicExportedFolder({
            folderName: `ExcelSpareParts_${timestamp}`,
            description: `Exportación de órdenes desde repuestos filtrados: ${fileName}`,
            orderCount: distinctOrderIds,
            sparePartCount: filteredSpareParts.length,
            totalSize: excelBuffer?.byteLength ?? 0,
            fileName
          }).subscribe({
            next: () => {},
            error: (registerError) => {
              console.error('Excel generado, pero falló el registro en Carpetas Exportadas:', registerError);
            }
          });

          this.showProgressMessage(`Se exportaron ${filteredSpareParts.length} filas (repuestos filtrados) con formato de órdenes`);

          this.removeExcelLoadingIndicator();
        } catch (error) {
          console.error('Error al exportar a Excel:', error);
          this.showErrorMessage('Error al exportar a Excel. Por favor, intente nuevamente.');

          this.removeExcelLoadingIndicator();
        }
      },
      error: (error) => {
        console.error('Error al obtener repuestos para exportar:', error);
        this.showErrorMessage('Error al obtener los datos. Por favor, intente nuevamente.');

        this.removeExcelLoadingIndicator();
      }
    });
  }

  /**
   * Exporta repuestos filtrados con TODAS las columnas de la tabla de repuestos.
   * Mantiene 1 fila por repuesto.
   */
  exportTableColumnsToExcel(): void {
    const count = this.filteredTotal;
    if (count === 0) {
      this.showErrorMessage('No hay datos para exportar');
      return;
    }

    if (!window.confirm(
      `Se exportarán ${count} repuesto(s) (una fila por repuesto), con todas las columnas de la tabla.\n\n¿Desea continuar?`
    )) {
      return;
    }

    const loadingIndicator = document.createElement('div');
    loadingIndicator.id = 'excel-loading-indicator';
    loadingIndicator.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 flex items-center';
    loadingIndicator.innerHTML = `
      <div class="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
      <span>Generando archivo Excel...</span>
    `;
    document.body.appendChild(loadingIndicator);

    this.sparePartService.downloadStatisticsExcel(
      this.searchTerm,
      this.sortedBy || undefined,
      this.sortOrder,
      this.getServerColumnFilters(),
      this.onlyLast60Days ? 60 : undefined,
      this.onlyLowSaleRatio
    ).pipe(
      finalize(() => this.removeExcelLoadingIndicator())
    ).subscribe({
      next: blob => {
        const date = new Date();
        const fileName = `Repuestos_estadisticas_${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}.xlsx`;
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
        this.showProgressMessage(`Se exportaron ${count} repuestos con datos de orden`);
      },
      error: error => {
        console.error('Error al exportar tabla de repuestos desde el backend:', error);
        this.showErrorMessage('Error al exportar a Excel. Por favor, intente nuevamente.');
      }
    });
  }

  /**
   * Formatea una fecha a DD/MM/YYYY
   */
  private formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  }

  /** Fecha y hora para filtros y Excel (EXPORTADO). */
  private formatDateTime(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${h}:${m}`;
  }

  formatArrivalDateTime(value: string | Date | null | undefined): string {
    return formatWallClockDateTime(value);
  }

  getAldanaColorHex(color: string | undefined | null): string {
    const normalized = (color || '').toLowerCase().trim();
    switch (normalized) {
      case 'rojo': return '#ef4444';
      case 'amarillo': return '#eab308';
      case 'amarillo claro': return '#ffff00';
      case 'azul': return '#3b82f6';
      case 'verde': return '#22c55e';
      case 'verde claro': return '#47ff51';
      case 'violeta': return '#8b5cf6';
      case 'naranja': return '#f97316';
      case 'rosado': return '#ec4899';
      case 'negro': return '#111827';
      case 'blanco': return '#ffffff';
      case 'marron': return '#92400e';
      default: return normalized || '#d1d5db';
    }
  }
}
